import { useEffect, useState, useMemo, useCallback, useRef, createContext, useContext } from "react";
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useBranch } from "../../context/BranchContext";
import { useDesktopTopBar } from "../../context/DesktopLayoutContext";
import DesktopLayout from "../layout/DesktopLayout";
import TopBar from "../layout/TopBar";
import Phone from "../Phone";
import BottomNav from "../BottomNav";
import "./EmployeeManagement.css";
import { T, useIsDesktop, Bone } from "../reports/reportTokens";
import { getEmployeeList, updateEmployeeStatus, deleteEmployeeApi, createEmployeeApi, updateEmployeeApi, getDevicePin, getLocationDetails, getEmployeeById, getRolesAndFunctions, getStaffPermissions } from "../../services/api";
import { locationDetailsCache, locationDetailsInFlight } from "../../services/locationDetailsCache";
import { isOnlySpecialCharacters, isLettersOnly, sanitizeLettersOnlyInput } from "../../utils/textValidation";

const employeeListInFlight = new Map<string, Promise<any>>();
const employeeDetailInFlight = new Map<string, Promise<any>>();
const rolesAndFunctionsInFlight = new Map<string, Promise<any>>();
const staffPermissionsInFlight = new Map<string, Promise<any>>();

/* ═══ Employee Context — shared state across route-based pages ═══ */
const EmployeeCtx = createContext<any>(null);
const useEmployeeCtx = () => useContext(EmployeeCtx);

/* ═══ Helpers ═══ */
const findToken = (obj: any, depth: any = 0) => {
    if (!obj || typeof obj !== "object" || depth > 3) return null;
    for (const key of ["accessToken", "access_token", "token", "jwtToken", "jwt", "bearerToken", "authToken"]) {
        if (obj[key] && typeof obj[key] === "string" && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === "string" && val.length > 50) return val;
        if (val && typeof val === "object" && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const findLocationId = (obj: any, depth: any = 0) => {
    if (!obj || typeof obj !== "object" || depth > 3) return null;
    for (const key of ["locationId", "location_id", "locationID", "locId", "outletId"]) {
        if (obj[key] && typeof obj[key] === "string") return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (val && typeof val === "object" && !Array.isArray(val)) {
            const found = findLocationId(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const Icon = ({ name, size = 18, color = T.t3, style = {} }: any) => (
    <span className="material-symbols-rounded" style={{ fontSize: size, lineHeight: 1, color, userSelect: "none", ...style }}>{name}</span>
);

/* ═══ Hero Banner ═══ */
function HeroBanner({ icon, title, subtitle, ctaLabel, ctaIcon, onCta, ctaDisabled, mobile }: any) {
    return (
        <div style={{
            background: "linear-gradient(135deg, rgba(229,35,51,0.06) 0%, rgba(229,35,51,0.02) 100%)",
            border: `1px solid ${T.cardBorder}`, borderRadius: 14,
            padding: mobile ? 16 : "24px 28px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            position: "relative", overflow: "hidden",
        }}>
            {/* Decorative blob */}
            <div style={{
                position: "absolute", top: -30, right: -30,
                width: 120, height: 120, borderRadius: "50%",
                background: "rgba(229,35,51,0.06)", pointerEvents: "none",
            }} />
            <div style={{ display: "flex", alignItems: "center", gap: mobile ? 12 : 16, position: "relative", zIndex: 1 }}>
                <div style={{
                    width: 56, height: 56, borderRadius: 14, flexShrink: 0,
                    background: "linear-gradient(135deg, #E52333, #E86B6B)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <Icon name={icon} size={26} color="#fff" />
                </div>
                <div>
                    <div style={{ fontSize: mobile ? 18 : 22, fontWeight: 700, color: T.t1 }}>{title}</div>
                    <div style={{ fontSize: 13, color: T.t3, marginTop: 2 }}>{subtitle}</div>
                </div>
            </div>
            {onCta && (
                <button disabled={ctaDisabled} onClick={() => !ctaDisabled && onCta()} style={{
                    display: "flex", alignItems: "center", gap: 6, padding: mobile ? "9px 14px" : "10px 20px",
                    borderRadius: 10, border: "none",
                    background: ctaDisabled ? T.t3 : T.accent, color: "#fff",
                    fontSize: 13, fontWeight: 600, cursor: ctaDisabled ? "not-allowed" : "pointer", fontFamily: T.font,
                    whiteSpace: "nowrap", position: "relative", zIndex: 1,
                    opacity: ctaDisabled ? 0.5 : 1,
                }}>
                    <Icon name={ctaIcon || "add"} size={16} color="#fff" />
                    {ctaLabel}
                </button>
            )}
        </div>
    );
}

/* ═══ Shimmer ═══ */
const shimmerGrid = "1.6fr 1.4fr 1fr 0.6fr 0.7fr 0.6fr 0.5fr";

function DesktopShimmer() {
    return (
        <div style={{ padding: 28, display: "flex", flexDirection: "column", gap: 24 }}>
            {/* Hero banner shimmer */}
            <div style={{
                background: "linear-gradient(135deg, rgba(229,35,51,0.06) 0%, rgba(229,35,51,0.02) 100%)",
                border: `1px solid ${T.cardBorder}`, borderRadius: 14,
                padding: "24px 28px", display: "flex", alignItems: "center", justifyContent: "space-between",
                position: "relative", overflow: "hidden",
            }}>
                <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(229,35,51,0.06)" }} />
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <div style={{ width: 56, height: 56, borderRadius: 14, background: "linear-gradient(135deg, #E52333, #E86B6B)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Icon name="group" size={26} color="#fff" />
                    </div>
                    <div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: T.t1 }}>Employees</div>
                        <div style={{ fontSize: 13, color: T.t3, marginTop: 2 }}>Manage your team members</div>
                    </div>
                </div>
                <button disabled style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 20px", borderRadius: 10, border: "none", background: T.accent, color: "#fff", fontSize: 13, fontWeight: 600, fontFamily: T.font }}>
                    <Icon name="person_add" size={16} color="#fff" /> + Add New
                </button>
            </div>

            {/* Search bar shimmer */}
            <div style={{ position: "relative" }}>
                <Icon name="search" size={18} color={T.t3} style={{ position: "absolute", left: 16, top: 13 }} />
                <input type="text" disabled placeholder="Search employees..."
                    style={{ width: "100%", border: `1px solid ${T.cardBorder}`, borderRadius: 24, padding: "12px 18px 12px 44px", fontSize: 13, fontFamily: T.font, color: T.t1, outline: "none", background: T.surface, boxSizing: "border-box" }} />
            </div>

            {/* Tabs shimmer */}
            <div>
                <div style={{ display: "flex", gap: 0 }}>
                    {["All", "Active", "Blocked"].map((t, ti) => (
                        <div key={t} style={{
                            padding: "10px 20px", fontSize: 13,
                            fontWeight: ti === 0 ? 600 : 400,
                            color: ti === 0 ? T.accent : T.t2,
                            borderBottom: ti === 0 ? `2px solid ${T.accent}` : "2px solid transparent",
                        }}>{t}</div>
                    ))}
                </div>
                <div style={{ borderBottom: `1px solid ${T.divider}` }} />
            </div>

            {/* Table Card shimmer */}
            <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, boxShadow: T.cardShadow, overflow: "hidden" }}>
                <div style={{ display: "grid", gridTemplateColumns: shimmerGrid, gap: 8, padding: "10px 14px", background: T.bg }}>
                    {["NAME", "ROLE", "STATUS", "EDIT", "UN/BLOCK", "DELETE", "VIEW"].map((h, i) => (
                        <div key={i} style={{ fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5 }}>{h}</div>
                    ))}
                </div>
                {[...Array(7)].map((_, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: shimmerGrid, gap: 8, alignItems: "center", padding: "12px 14px", borderBottom: i < 6 ? `1px solid ${T.divider}` : "none" }}>
                        <Bone h={13} w={110} r={4} />
                        <Bone h={12} w={100} r={4} />
                        <Bone h={22} w={60} r={4} />
                        <Bone h={12} w={28} r={4} />
                        <Bone h={12} w={32} r={4} />
                        <Bone h={12} w={36} r={4} />
                        <Bone h={12} w={28} r={4} />
                    </div>
                ))}
            </div>
        </div>
    );
}

function MobileShimmer() {
    return (
        <div style={{ padding: "12px 16px 88px", display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Hero banner shimmer */}
            <div style={{
                background: "linear-gradient(135deg, rgba(229,35,51,0.06) 0%, rgba(229,35,51,0.02) 100%)",
                border: `1px solid ${T.cardBorder}`, borderRadius: 14,
                padding: 16, display: "flex", alignItems: "center", justifyContent: "space-between",
                position: "relative", overflow: "hidden",
            }}>
                <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(229,35,51,0.06)" }} />
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 56, height: 56, borderRadius: 14, background: "linear-gradient(135deg, #E52333, #E86B6B)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Icon name="group" size={26} color="#fff" />
                    </div>
                    <div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: T.t1 }}>Employees</div>
                        <div style={{ fontSize: 13, color: T.t3, marginTop: 2 }}>Manage your team</div>
                    </div>
                </div>
            </div>
            {/* Search + Add */}
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div style={{ position: "relative", flex: 1 }}>
                    <Icon name="search" size={16} color={T.t3} style={{ position: "absolute", left: 12, top: 10 }} />
                    <input type="text" disabled placeholder="Search employees..."
                        style={{ width: "100%", border: `1px solid ${T.cardBorder}`, borderRadius: 24, padding: "9px 12px 9px 34px", fontSize: 12, fontFamily: T.font, color: T.t1, outline: "none", background: T.surface, boxSizing: "border-box" }} />
                </div>
                <button disabled style={{
                    display: "flex", alignItems: "center", gap: 4, padding: "9px 14px", borderRadius: 12,
                    border: "none", background: T.accent, color: "#fff", fontSize: 12, fontWeight: 600,
                    fontFamily: T.font, whiteSpace: "nowrap", flexShrink: 0,
                }}>
                    <Icon name="add" size={16} color="#fff" /> Add
                </button>
            </div>
            {/* Status tabs */}
            <div style={{ display: "flex", gap: 6 }}>
                {["All", "Active", "Blocked"].map((t, ti) => (
                    <button key={t} disabled style={{
                        flex: 1, padding: "7px 0", borderRadius: 10, fontSize: 11, fontWeight: 600,
                        border: ti === 0 ? `1.5px solid ${T.accent}` : `1px solid ${T.cardBorder}`,
                        background: ti === 0 ? T.accentSoft : T.surface,
                        color: ti === 0 ? T.accent : T.t2,
                        fontFamily: T.font,
                    }}>{t}</button>
                ))}
            </div>
            {/* Employee cards shimmer */}
            {[...Array(5)].map((_, i) => (
                <div key={i} style={{ background: T.card, borderRadius: 14, padding: "14px 16px", boxShadow: T.shadow }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <Bone h={38} w={38} r={12} />
                        <div style={{ flex: 1 }}>
                            <Bone h={14} w="55%" r={4} mb={6} />
                            <Bone h={10} w="35%" r={3} />
                        </div>
                        <Bone h={22} w={52} r={4} />
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ═══ Status Badge ═══ */
function StatusBadge({ isActive }: any) {
    const active = isActive !== false;
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "3px 10px", borderRadius: 4,
            fontSize: 11, fontWeight: 600, letterSpacing: 0.3, textTransform: "uppercase",
            background: active ? T.greenSoft : T.orangeSoft,
            color: active ? T.green : T.orange,
        }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: active ? T.green : T.orange }} />
            {active ? "ACTIVE" : "BLOCKED"}
        </span>
    );
}

/* ═══ Confirm Modal ═══ */
function ConfirmModal({ open, icon, iconColor, title, message, onConfirm, onCancel, confirmLabel, confirmColor, loading }: any) {
    if (!open) return null;
    return (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(15,15,20,0.45)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }} onClick={onCancel}>
            <div onClick={e => e.stopPropagation()} style={{
                background: T.card, borderRadius: 20, padding: "28px 32px", maxWidth: 400, width: "90%",
                boxShadow: "0 12px 48px rgba(0,0,0,0.18)", fontFamily: T.font, animation: "fadeIn .15s ease",
            }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: `${iconColor}14`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                    <Icon name={icon} size={24} color={iconColor} />
                </div>
                <div style={{ fontSize: 17, fontWeight: 700, color: T.t1, marginBottom: 8 }}>{title}</div>
                <div style={{ fontSize: 13, color: T.t2, lineHeight: 1.6, marginBottom: 28 }}>{message}</div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <button onClick={onCancel} disabled={loading} style={{
                        padding: "9px 22px", borderRadius: 10, border: `1px solid ${T.cardBorder}`,
                        background: T.surface, color: T.t2, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.font,
                        transition: "all .15s",
                    }}>Cancel</button>
                    <button onClick={onConfirm} disabled={loading} style={{
                        padding: "9px 22px", borderRadius: 10, border: "none",
                        background: confirmColor, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.font,
                        opacity: loading ? 0.6 : 1, transition: "all .15s",
                    }}>{loading ? "Processing..." : confirmLabel}</button>
                </div>
            </div>
        </div>
    );
}

/* ═══ Form Field ═══ */
function FormField({ label, required, children, error }: any) {
    return (
        <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: T.t2, marginBottom: 6, display: "block" }}>
                {label} {required && <span style={{ color: T.red }}>*</span>}
            </label>
            {children}
            {error && <div style={{ fontSize: 10, color: T.red, marginTop: 3 }}>{error}</div>}
        </div>
    );
}

const inputStyle: any = {
    width: "100%", padding: "10px 14px", borderRadius: 8, fontSize: 13,
    border: `1px solid ${T.cardBorder}`, fontFamily: T.font, color: T.t1,
    outline: "none", background: T.surface, boxSizing: "border-box", transition: "border .15s",
};

/* ═══ Phone mask helper (999-999-9999) ═══ */
const formatPhone = (val: any) => {
    const digits = val.replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const onlySpecialCharacterTextError = "Cannot contain only special characters";

const DOB_MIN_DATE_ISO = "1900-01-01";
const DOB_MIN_AGE_YEARS = 18;

const toIsoDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const isValidIsoDate = (isoDate: any) => {
    if (typeof isoDate !== "string") return false;
    const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return false;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return false;
    const normalized = new Date(year, month - 1, day);
    return normalized.getFullYear() === year
        && normalized.getMonth() === month - 1
        && normalized.getDate() === day;
};

const normalizeDateOfBirth = (value: any) => {
    if (value == null || value === "") return "";

    if (value instanceof Date) {
        if (Number.isNaN(value.getTime())) return "";
        return toIsoDate(value);
    }

    if (typeof value === "number") {
        const parsedFromNumber = new Date(value);
        if (Number.isNaN(parsedFromNumber.getTime())) return "";
        return toIsoDate(parsedFromNumber);
    }

    if (typeof value !== "string") return "";
    const raw = value.trim();
    if (!raw) return "";

    const normalized = raw.replace(/\//g, "-");

    const ymd = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:\D.*)?$/);
    if (ymd) {
        const iso = `${ymd[1]}-${String(ymd[2]).padStart(2, "0")}-${String(ymd[3]).padStart(2, "0")}`;
        return isValidIsoDate(iso) ? iso : "";
    }

    const dmy = normalized.match(/^(\d{1,2})-(\d{1,2})-(\d{4})(?:\D.*)?$/);
    if (dmy) {
        const iso = `${dmy[3]}-${String(dmy[2]).padStart(2, "0")}-${String(dmy[1]).padStart(2, "0")}`;
        return isValidIsoDate(iso) ? iso : "";
    }

    if (/^\d{4}-\d{2}-\d{2}T/.test(normalized)) {
        const iso = normalized.slice(0, 10);
        return isValidIsoDate(iso) ? iso : "";
    }

    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) return "";
    return toIsoDate(parsed);
};

const formatDateOfBirth = (value: any) => {
    const iso = normalizeDateOfBirth(value);
    if (!iso) return "--";
    const [year, month, day] = iso.split("-");
    return `${day}-${month}-${year}`;
};

const getDobMaximumDateIso = () => {
    const maxDate = new Date();
    maxDate.setHours(0, 0, 0, 0);
    maxDate.setFullYear(maxDate.getFullYear() - DOB_MIN_AGE_YEARS);
    return toIsoDate(maxDate);
};

const isDob18OrAbove = (value: any) => {
    const iso = normalizeDateOfBirth(value);
    if (!iso) return false;
    return iso <= getDobMaximumDateIso();
};

// Normalize API/network/offline failures into one user-facing fetch error message.
const getEmployeeFetchErrorMessage = (err: any) => {
    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    const rawMessage = String(err?.message || "").toLowerCase();
    const isNetworkError = offline
        || rawMessage.includes("network")
        || rawMessage.includes("failed to fetch")
        || rawMessage.includes("load failed")
        || rawMessage.includes("internet");
    if (isNetworkError) return "Internet connection lost. Please check your network and try again.";
    return "Unable to load employees right now. Please try again.";
};

/* ═══ Section Header (for form cards) ═══ */
function SectionHeader({ icon, title, subtitle }: any) {
    return (
        <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <Icon name={icon} size={18} color={T.accent} />
                <span style={{ fontSize: 16, fontWeight: 700, color: T.t1 }}>{title}</span>
            </div>
            {subtitle && <div style={{ fontSize: 13, color: T.t3, marginLeft: 26 }}>{subtitle}</div>}
            <div style={{ borderBottom: `1px solid ${T.divider}`, marginTop: 12 }} />
        </div>
    );
}

/* ═══ Breadcrumb ═══ */
function Breadcrumb({ items }: any) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16, fontSize: 13, color: T.t3 }}>
            {items.map((item, i) => (
                <span key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {i > 0 && <Icon name="chevron_right" size={16} color={T.t3} />}
                    <span style={{
                        color: i === items.length - 1 ? T.t1 : T.t3,
                        fontWeight: i === items.length - 1 ? 600 : 400,
                        cursor: item.onClick ? "pointer" : "default",
                    }} onClick={item.onClick}>{item.label}</span>
                </span>
            ))}
        </div>
    );
}

/* ═══ Roles/Functions Editor (full-page overlay) ═══ */
function RolesFunctionsEditor({ token, selectedRole, checkedFunctions, setCheckedFunctions, rolesDataRef, skipAutoPopulate }: any) {
    const [rolesData, setRolesData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageOpen, setPageOpen] = useState(false);
    const [localChecked, setLocalChecked] = useState([]);

    // Fetch roles/functions from /api-info
    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        setLoading(true);
        if (!rolesAndFunctionsInFlight.has(token)) {
            rolesAndFunctionsInFlight.set(token, getRolesAndFunctions({ token }));
        }
        rolesAndFunctionsInFlight.get(token)!.then(res => {
            rolesAndFunctionsInFlight.delete(token);
            if (cancelled) return;
            const data = res?.metaDataInfo?.data ?? res?.data ?? res;
            if (Array.isArray(data)) {
                setRolesData(data);
                if (rolesDataRef) rolesDataRef.current = data;
            }
        }).catch(() => { rolesAndFunctionsInFlight.delete(token); })
          .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [token, rolesDataRef]);

    // When role changes, auto-check default functions for that role
    // When role changes, auto-check default functions for that role (skipped in edit mode where API permissions are used)
    useEffect(() => {
        if (skipAutoPopulate || !selectedRole || rolesData.length === 0) return;
        const defaults = rolesData
            .flatMap(m => m.functionality || [])
            .filter(f => f.roles?.includes(selectedRole))
            .map(f => (f.displayName || "").toLowerCase())
            .filter(Boolean);
        setCheckedFunctions(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRole, rolesData, skipAutoPopulate]);

    const openPage = () => { setLocalChecked([...checkedFunctions]); setPageOpen(true); };
    const handleSave = () => { setCheckedFunctions(localChecked); setPageOpen(false); };
    const handleCancel = () => setPageOpen(false);

    const isFunctionChecked = (name: any) => localChecked.includes(name.toLowerCase());
    const handleCheckboxChange = (name: any) => {
        const fn = name.toLowerCase();
        setLocalChecked(prev => prev.includes(fn) ? prev.filter(f => f !== fn) : [...prev, fn]);
    };
    const isModuleChecked = (moduleName: any) => {
        const mod = rolesData.find(m => m.module?.toLowerCase() === moduleName.toLowerCase());
        if (!mod) return false;
        return (mod.functionality || []).every(f => f.displayName && localChecked.includes(f.displayName.toLowerCase()));
    };
    const isModulePartial = (moduleName: any) => {
        const mod = rolesData.find(m => m.module?.toLowerCase() === moduleName.toLowerCase());
        if (!mod) return false;
        const fns = (mod.functionality || []).filter(f => f.displayName);
        const checked = fns.filter(f => localChecked.includes(f.displayName.toLowerCase()));
        return checked.length > 0 && checked.length < fns.length;
    };
    const handleModuleToggle = (moduleName: any) => {
        const mod = rolesData.find(m => m.module?.toLowerCase() === moduleName.toLowerCase());
        if (!mod) return;
        const fns = (mod.functionality || []).map(f => (f.displayName || "").toLowerCase()).filter(Boolean);
        setLocalChecked(prev => {
            if (fns.every(f => prev.includes(f))) return prev.filter(f => !fns.includes(f));
            return [...prev, ...fns.filter(f => !prev.includes(f))];
        });
    };
    const handleReset = () => {
        const defaults = rolesData
            .flatMap(m => m.functionality || [])
            .filter(f => f.roles?.includes(selectedRole))
            .map(f => (f.displayName || "").toLowerCase())
            .filter(Boolean);
        setLocalChecked(defaults);
    };
    const totalFuncs = rolesData.reduce((s, m) => s + (m.functionality || []).length, 0);
    const checkedCount = rolesData.reduce((s, m) =>
        s + (m.functionality || []).filter(f => f.displayName && localChecked.includes(f.displayName.toLowerCase())).length, 0);
    const savedCheckedCount = rolesData.reduce((s, m) =>
        s + (m.functionality || []).filter(f => f.displayName && checkedFunctions.includes(f.displayName.toLowerCase())).length, 0);

    if (!selectedRole) return null;

    return (
        <div style={{ marginTop: 4, marginBottom: 16 }}>
            {/* Trigger button */}
            <button type="button" onClick={pageOpen ? handleCancel : openPage} style={{
                display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer",
                fontSize: 12, fontWeight: 600, color: T.accent, padding: "7px 16px",
                borderRadius: 10, border: `1px solid ${T.accent}30`, background: `${T.accent}08`,
                fontFamily: T.font, transition: "all .15s",
            }}>
                <Icon name={pageOpen ? "expand_less" : "tune"} size={16} color={T.accent} />
                {pageOpen ? "Collapse Roles/Functions" : "Edit Roles/Functions"}
                {savedCheckedCount > 0 && (
                    <span style={{
                        fontSize: 10, fontWeight: 700, color: "#fff", background: T.accent,
                        borderRadius: 10, padding: "2px 7px", marginLeft: 2,
                    }}>{savedCheckedCount}</span>
                )}
            </button>

            {/* Inline expanded roles editor */}
            {pageOpen && (
                <div style={{
                    marginTop: 16, background: T.bg, border: `1px solid ${T.cardBorder}`,
                    borderRadius: 14, overflow: "hidden", fontFamily: T.font, animation: "fadeInUp .15s ease",
                }}>
                    {/* Inline header */}
                    <div style={{
                        padding: "14px 20px", background: T.card,
                        borderBottom: `1px solid ${T.divider}`,
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        flexWrap: "wrap", gap: 10,
                    }}>
                        <div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: T.t1, display: "flex", alignItems: "center", gap: 8 }}>
                                <Icon name="tune" size={18} color={T.accent} />
                                Roles & Functions
                            </div>
                            <div style={{ fontSize: 12, color: T.t3, marginTop: 2, marginLeft: 26 }}>
                                {selectedRole.replace(/_/g, " ")} — {checkedCount} of {totalFuncs} selected
                            </div>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                            <button type="button" onClick={handleReset} style={{
                                padding: "7px 14px", borderRadius: 8, border: `1px solid ${T.cardBorder}`,
                                background: T.surface, color: T.t2, fontSize: 12, fontWeight: 600,
                                cursor: "pointer", fontFamily: T.font, display: "flex", alignItems: "center", gap: 5,
                            }}>
                                <Icon name="restart_alt" size={14} color={T.t2} /> Reset
                            </button>
                            <button type="button" onClick={handleSave} style={{
                                padding: "7px 16px", borderRadius: 8, border: "none",
                                background: T.accent, color: "#fff", fontSize: 12, fontWeight: 600,
                                cursor: "pointer", fontFamily: T.font, display: "flex", alignItems: "center", gap: 5,
                            }}>
                                <Icon name="check" size={14} color="#fff" /> Save
                            </button>
                        </div>
                    </div>

                    {/* Modules grid */}
                    <div style={{ padding: "16px 20px" }}>
                        {loading ? (
                            <div style={{ padding: 32, textAlign: "center", color: T.t3, fontSize: 13 }}>
                                <Icon name="hourglass_empty" size={28} color={T.t3} style={{ display: "block", margin: "0 auto 10px" }} />
                                Loading roles and functions...
                            </div>
                        ) : rolesData.length === 0 ? (
                            <div style={{ padding: 32, textAlign: "center", color: T.t3, fontSize: 13 }}>
                                <Icon name="info" size={28} color={T.t3} style={{ display: "block", margin: "0 auto 10px" }} />
                                No roles and functions data found
                            </div>
                        ) : (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
                                {rolesData.map(mod => {
                                    const funcs = mod.functionality || [];
                                    const allChecked = isModuleChecked(mod.module);
                                    const partial = isModulePartial(mod.module);
                                    const moduleCheckedCount = funcs.filter(f => f.displayName && localChecked.includes(f.displayName.toLowerCase())).length;
                                    return (
                                        <div key={mod.module} style={{
                                            background: T.card, border: `1px solid ${T.cardBorder}`,
                                            borderRadius: 12, overflow: "hidden", boxShadow: T.cardShadow,
                                        }}>
                                            {/* Module header */}
                                            <div onClick={() => handleModuleToggle(mod.module)} style={{
                                                padding: "12px 16px", display: "flex", alignItems: "center", gap: 10,
                                                cursor: "pointer", borderBottom: `1px solid ${T.divider}`,
                                                background: allChecked ? `${T.accent}06` : "transparent",
                                                transition: "background .15s",
                                            }}>
                                                <div style={{
                                                    width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                                                    border: `2px solid ${allChecked || partial ? T.accent : T.cardBorder}`,
                                                    background: allChecked ? T.accent : "transparent",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    transition: "all .15s",
                                                }}>
                                                    {allChecked && <Icon name="check" size={12} color="#fff" />}
                                                    {partial && !allChecked && <div style={{ width: 8, height: 2, background: T.accent, borderRadius: 1 }} />}
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: T.t1 }}>{mod.module}</div>
                                                </div>
                                                <span style={{
                                                    fontSize: 10, fontWeight: 600, color: allChecked ? T.accent : T.t3,
                                                    background: allChecked ? `${T.accent}12` : T.bg,
                                                    padding: "2px 8px", borderRadius: 8,
                                                }}>{moduleCheckedCount}/{funcs.length}</span>
                                            </div>
                                            {/* Functions */}
                                            <div style={{ padding: "6px 10px" }}>
                                                {funcs.map(func => {
                                                    const fname = func.displayName || func.name || "";
                                                    const checked = isFunctionChecked(fname);
                                                    return (
                                                        <label key={fname} onClick={() => handleCheckboxChange(fname)} style={{
                                                            display: "flex", alignItems: "center", gap: 8, padding: "7px 8px",
                                                            cursor: "pointer", borderRadius: 6,
                                                            background: checked ? `${T.accent}06` : "transparent",
                                                            transition: "background .1s", marginBottom: 1,
                                                        }}>
                                                            <div style={{
                                                                width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                                                                border: `1.5px solid ${checked ? T.accent : T.cardBorder}`,
                                                                background: checked ? T.accent : "transparent",
                                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                                transition: "all .15s",
                                                            }}>
                                                                {checked && <Icon name="check" size={10} color="#fff" />}
                                                            </div>
                                                            <span style={{ fontSize: 12, color: checked ? T.t1 : T.t2, fontWeight: checked ? 500 : 400 }}>{fname}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ═══ Restaurant Roles (matching maghil-portal) ═══ */
const RESTAURANT_ROLES = [
    "Chef", "Restaurant_Manager", "Restaurant_Owner", "Regional_Employee",
    "Supervisor", "Waiter","Bartender", "Host", "Delivery", "Cashier"
];

/* ═══ Add Employee Page (full-page overlay) ═══ */
function AddEmployeePanel({ open, onClose, onSave, token, locationId, merchantId, businessName, mobile, branches, currentBranchName, actionPerformedByStaffId }: any) {
    const INITIAL = {
        firstName: "", lastName: "", nickName: "", useNickname: false,
        mobileNumber: "", email: "", address1: "", address2: "",
        education: "", dateOfBirth: "",
        outlet: currentBranchName || "", role: "", userId: "", password: "", pin: "",
    };
    const [form, setForm] = useState(INITIAL);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<any>({});
    const [showPassword, setShowPassword] = useState(false);
    const [showPin, setShowPin] = useState(true);
    const [pinLoading, setPinLoading] = useState(false);
    const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
    const [checkedFunctions, setCheckedFunctions] = useState([]);
    const rolesDataRef = useRef([]);
    const dobMaxDate = useMemo(() => getDobMaximumDateIso(), []);

    // Build userAccessInfoList in the portal format: [{ moduleType, moduleName, urls }]
    const buildAccessInfoList = () => {
        const roles = rolesDataRef.current;
        if (!roles || roles.length === 0) return [];
        return roles
            .map(mod => {
                const moduleFuncs = (mod.functionality || [])
                    .filter(f => f.displayName && checkedFunctions.includes(f.displayName.toLowerCase()))
                    .map(f => ({
                        moduleType: mod.module,
                        moduleName: f.name,
                        urls: f.urls || [],
                    }));
                return moduleFuncs.length > 0 ? moduleFuncs : null;
            })
            .flat()
            .filter(Boolean);
    };

    useEffect(() => {
        if (!open) return;
        setForm({ ...INITIAL, outlet: currentBranchName || "" });
        setErrors({});
        setShowPassword(false);
        setShowPin(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open || !merchantId || !token) return;
        setPinLoading(true);
        getDevicePin({ merchantId, token }).then(res => {
            const pin = res?.devicePin ?? res?.metaDataInfo?.data ?? res?.data ?? res?.pin ?? res;
            if (pin != null) setForm(f => ({ ...f, pin: String(pin).padStart(4, "0") }));
        }).catch(() => {}).finally(() => setPinLoading(false));
    }, [open, token, merchantId]);

    const set = (key: any, val: any) => setForm(f => ({ ...f, [key]: val }));

    const refreshPin = () => {
        if (!merchantId || !token) return;
        setPinLoading(true);
        getDevicePin({ merchantId, token }).then(res => {
            const pin = res?.devicePin ?? res?.metaDataInfo?.data ?? res?.data ?? res?.pin ?? res;
            if (pin != null) set("pin", String(pin).padStart(4, "0"));
        }).catch(() => {}).finally(() => setPinLoading(false));
    };

    const handleClearAll = () => {
        setForm({ ...INITIAL, outlet: currentBranchName || "" });
        setErrors({});
        setShowPassword(false);
        setShowPin(true);
        setClearConfirmOpen(false);
        // Re-generate PIN
        refreshPin();
    };

    const validate = () => {
        const e: any = {};
        const firstName = String(form.firstName ?? "").trim();
        const lastName = String(form.lastName ?? "").trim();
        const nickName = String(form.nickName ?? "").trim();
        const education = String(form.education ?? "").trim();
        const address1 = String(form.address1 ?? "").trim();
        const address2 = String(form.address2 ?? "").trim();
        const userId = String(form.userId ?? "").trim();
        const phoneDigits = String(form.mobileNumber ?? "").replace(/\D/g, "");
        if (!firstName || firstName.length < 2) e.firstName = "Min 2 characters, letters only";
        if (firstName && !isLettersOnly(firstName)) e.firstName = "Letters only";
        if (lastName && !isLettersOnly(lastName)) e.lastName = "Letters only";
        if (nickName && isOnlySpecialCharacters(nickName)) e.nickName = onlySpecialCharacterTextError;
        if (education && isOnlySpecialCharacters(education)) e.education = onlySpecialCharacterTextError;
        if (address1 && isOnlySpecialCharacters(address1)) e.address1 = onlySpecialCharacterTextError;
        if (address2 && isOnlySpecialCharacters(address2)) e.address2 = onlySpecialCharacterTextError;
        if (phoneDigits.length > 0 && phoneDigits.length < 10) e.mobileNumber = "Phone number must be at least 10 digits";
        if (!form.outlet) e.outlet = "Outlet is required";
        if (!form.role) e.role = "Role is required";
        if (!form.userId.trim()) e.userId = "User ID is required";
        if (/\s/.test(form.userId)) e.userId = "No spaces allowed";
        if (userId && isOnlySpecialCharacters(userId)) e.userId = "User ID cannot contain only special characters";
        // Add flow requires password; block profile creation when left empty.
        if (!form.password.trim()) e.password = "Password is required";
        if (form.password && form.password.length < 8) e.password = "Min 8 chars, 1 uppercase, 1 number, 1 special";
        if (form.password && !/^(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/.test(form.password)) e.password = "Min 8 chars, 1 uppercase, 1 number, 1 special";
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
        if (normalizeDateOfBirth(form.dateOfBirth) && !isDob18OrAbove(form.dateOfBirth)) e.dateOfBirth = "Employee must be 18 years or older";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const address = [form.address1.trim(), form.address2.trim()].filter(Boolean).join(" ").trim() || null;
            const accessInfoList = buildAccessInfoList();
            // Match portal: resolve locationId from branches by outlet name
            const resolvedLocationId = (branches || []).find(b =>
                (b.locationName || "").includes(form.outlet)
            )?.id || locationId;
            const payload = {
                firstName: form.firstName.trim(),
                lastName: (form.lastName || "").trim(),
                fullName: `${form.firstName.trim()} ${(form.lastName || "").trim()}`.trim(),
                nickName: form.nickName.trim(),
                isToUseNickName: form.useNickname,
                role: form.role,
                businessName,
                userId: form.userId.trim(),
                password: form.password || null,
                email: form.email.trim() || null,
                mobileNumber: form.mobileNumber.replace(/-/g, "") || "",
                address,
                education: form.education.trim() || null,
                dateOfBirth: normalizeDateOfBirth(form.dateOfBirth) || null,
                devicePin: form.pin || null,
                merchantId,
                locationId: resolvedLocationId,
                IsTempPassword: false,
                isDefaultFunctionalityAccessUpdated: accessInfoList.length > 0,
                userAccessInfoList: accessInfoList,
            };
            await createEmployeeApi({ details: payload, token, actionPerformedByStaffId });
            if (locationId) employeeListInFlight.delete(locationId);
            await onSave?.();
            onClose();
        } catch (err) {
            setErrors({ submit: err.message || "Failed to create employee" });
        } finally { setSaving(false); }
    };

    if (!open) return null;

    return (
        <div style={{ fontFamily: T.font, animation: "fadeInUp .2s ease" }}>
            {/* Form body — renders inline within layout */}
            <div style={{ padding: mobile ? "16px" : "24px 32px" }}>

                {/* Breadcrumb */}
                <Breadcrumb items={[
                    { label: "Employees", onClick: onClose },
                    { label: "Add New Employee" },
                ]} />

                {/* Hero Banner */}
                <div style={{ marginBottom: 24 }}>
                    <HeroBanner
                        icon="person_add"
                        title="Add New Employee"
                        subtitle="Fill in the details to create a new employee"
                        mobile={mobile}
                    />
                </div>

                {/* ── Personal Info ── */}
                <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: mobile ? "16px" : "24px 28px", boxShadow: T.cardShadow, marginBottom: 20 }}>
                    <SectionHeader icon="person" title="Personal Information" subtitle="Basic details about the employee" />

                    <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: "0 20px" }}>
                        <FormField label="First Name" required error={errors.firstName}>
                            <input style={inputStyle} value={form.firstName} onChange={e => set("firstName", sanitizeLettersOnlyInput(e.target.value))} placeholder="First name" />
                        </FormField>
                        <FormField label="Last Name" error={errors.lastName}>
                            <input style={inputStyle} value={form.lastName} onChange={e => set("lastName", sanitizeLettersOnlyInput(e.target.value))} placeholder="Last name" />
                        </FormField>
                        <FormField label="Nick Name" error={errors.nickName}>
                            <input style={inputStyle} value={form.nickName} onChange={e => set("nickName", e.target.value)} placeholder="Nick name" />
                        </FormField>
                    </div>

                    <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, cursor: "pointer", fontSize: 12, color: T.t2 }}>
                        <input type="checkbox" checked={form.useNickname} onChange={e => set("useNickname", e.target.checked)}
                            style={{ accentColor: T.accent, width: 16, height: 16 }} />
                        Utilize nickname in all forthcoming activities
                    </label>

                    <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: "0 20px" }}>
                        <FormField label="Phone" error={errors.mobileNumber}>
                            <input style={inputStyle} type="tel" value={form.mobileNumber}
                                onChange={e => {
                                    const formatted = formatPhone(e.target.value);
                                    const digits = formatted.replace(/\D/g, "");
                                    set("mobileNumber", formatted);
                                    setErrors(prev => ({
                                        ...prev,
                                        mobileNumber: digits.length > 0 && digits.length < 10 ? "Phone number must be at least 10 digits" : undefined,
                                    }));
                                }}
                                placeholder="999-999-9999" maxLength={12} />
                        </FormField>
                        <FormField label="Email" error={errors.email}>
                            <input style={inputStyle} type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="Email address" />
                        </FormField>
                        <FormField label="Education" error={errors.education}>
                            <input style={inputStyle} value={form.education} onChange={e => set("education", e.target.value)} placeholder="Education" />
                        </FormField>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: "0 20px" }}>
                        <FormField label="Address Line 1" error={errors.address1}>
                            <input style={inputStyle} value={form.address1} onChange={e => set("address1", e.target.value)} placeholder="Address line 1" />
                        </FormField>
                        <FormField label="Address Line 2" error={errors.address2}>
                            <input style={inputStyle} value={form.address2} onChange={e => set("address2", e.target.value)} placeholder="Address line 2" />
                        </FormField>
                        <FormField label="Date of Birth" error={errors.dateOfBirth}>
                            <input style={inputStyle} type="date" value={normalizeDateOfBirth(form.dateOfBirth)}
                                onChange={e => set("dateOfBirth", normalizeDateOfBirth(e.target.value))}
                                min={DOB_MIN_DATE_ISO} max={dobMaxDate} />
                        </FormField>
                    </div>
                </div>

                {/* ── Formal Setup ── */}
                <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: mobile ? "16px" : "24px 28px", boxShadow: T.cardShadow, marginBottom: 20 }}>
                    <SectionHeader icon="settings" title="Formal Setup" subtitle="Role, access credentials and permissions" />

                    <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: "0 20px" }}>
                        <FormField label="Outlet" required error={errors.outlet}>
                            <select style={{ ...inputStyle, cursor: branches && branches.length > 1 ? "pointer" : "default" }}
                                value={form.outlet}
                                onChange={e => set("outlet", e.target.value)}
                                disabled={!branches || branches.length <= 1}>
                                <option value="">Select outlet</option>
                                {branches && branches.length > 0 ? branches.map(b => {
                                    const name = b.locationName ?? b.name ?? b.branchName ?? "Branch";
                                    return <option key={b.id ?? b.locationId} value={name}>{name}</option>;
                                }) : currentBranchName ? <option value={currentBranchName}>{currentBranchName}</option> : null}
                            </select>
                        </FormField>
                        <FormField label="Roles" required error={errors.role}>
                            <select style={{ ...inputStyle, cursor: "pointer" }} value={form.role} onChange={e => set("role", e.target.value)}>
                                <option value="">Select role</option>
                                {RESTAURANT_ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                            </select>
                        </FormField>
                        <FormField label="User ID" required error={errors.userId}>
                            <input style={inputStyle} value={form.userId} onChange={e => set("userId", e.target.value.replace(/\s/g, ""))} placeholder="User ID for login" autoComplete="off" />
                        </FormField>
                    </div>
                    <RolesFunctionsEditor token={token} selectedRole={form.role} checkedFunctions={checkedFunctions} setCheckedFunctions={setCheckedFunctions} rolesDataRef={rolesDataRef} />

                    <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: "0 20px" }}>
                        <FormField label="Password" required error={errors.password}>
                            <div style={{ position: "relative" }}>
                                <input className="employee-password-input" style={{ ...inputStyle, paddingRight: 40 }} type={showPassword ? "text" : "password"}
                                    value={form.password} onChange={e => {
                                        const val = e.target.value;
                                        set("password", val);
                                        let pwdErr = undefined;
                                        if (!val.trim()) pwdErr = "Password is required";
                                        else if (val.length < 8 || !/^(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/.test(val)) pwdErr = "Min 8 chars, 1 uppercase, 1 number, 1 special";
                                        setErrors(prev => ({ ...prev, password: pwdErr }));
                                    }}
                                    placeholder="Min 8 chars, 1 upper, 1 number, 1 special" autoComplete="new-password" />
                                <button type="button" onClick={() => setShowPassword(p => !p)} style={{
                                    position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                                    background: "none", border: "none", cursor: "pointer", padding: 4,
                                }}>
                                    <Icon name={showPassword ? "visibility" : "visibility_off"} size={16} color={T.t3} />
                                </button>
                            </div>
                        </FormField>
                        <FormField label="Create Pin">
                            <div className="employee-pin-row">
                                <div className="employee-pin-digits">
                                    {[0, 1, 2, 3].map(idx => (
                                        <input key={idx} style={{
                                            ...inputStyle, width: 44, textAlign: "center", fontSize: 18, fontWeight: 700,
                                            letterSpacing: 0, padding: "10px 0",
                                        }} type={showPin ? "text" : "password"} value={form.pin[idx] || ""} disabled placeholder="-" readOnly />
                                    ))}
                                </div>
                                <div className="employee-pin-actions">
                                    <button type="button" onClick={() => setShowPin(p => !p)} className="employee-pin-icon-btn">
                                        <Icon name={showPin ? "visibility" : "visibility_off"} size={16} color={T.t2} />
                                    </button>
                                    <button type="button" onClick={refreshPin} disabled={pinLoading} className="employee-pin-icon-btn">
                                        <span className={pinLoading ? "employee-pin-spin" : ""}>
                                            <Icon name="refresh" size={16} color={T.t2} />
                                        </span>
                                    </button>
                                </div>
                            </div>
                            <div style={{ fontSize: 10, color: T.t3, marginTop: 4 }}>The four digit pin will be used for validating the user</div>
                        </FormField>
                    </div>
                </div>

                {errors.submit && (
                    <div style={{ padding: "12px 16px", borderRadius: 12, background: T.redSoft, color: T.red, fontSize: 13, fontWeight: 600, marginBottom: 20 }}>
                        {errors.submit}
                    </div>
                )}

                {/* Bottom action buttons */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingBottom: 24 }}>
                    <button type="button" onClick={() => setClearConfirmOpen(true)} disabled={saving} style={{
                        padding: "10px 22px", borderRadius: 10, border: `1px solid ${T.cardBorder}`,
                        background: T.surface, color: T.t2, fontSize: 13, fontWeight: 600,
                        cursor: "pointer", fontFamily: T.font, display: "flex", alignItems: "center", gap: 6,
                    }}>
                        <Icon name="clear_all" size={16} color={T.t2} /> Clear All
                    </button>
                    <button type="button" onClick={handleSave} disabled={saving} style={{
                        padding: "10px 24px", borderRadius: 10, border: "none",
                        background: T.accent, color: "#fff", fontSize: 13, fontWeight: 600,
                        cursor: "pointer", fontFamily: T.font, display: "flex", alignItems: "center", gap: 6,
                        opacity: saving ? 0.6 : 1,
                    }}>
                        <Icon name="person_add" size={16} color="#fff" />
                        {saving ? "Saving..." : "Add Employee"}
                    </button>
                </div>
            </div>

            {/* Clear All Confirmation Modal */}
            <ConfirmModal
                open={clearConfirmOpen}
                icon="warning"
                iconColor={T.orange}
                title="Clear All Fields?"
                message="Are you sure you want to clear all input fields? Any unsaved changes will be lost."
                confirmLabel="Clear All"
                confirmColor={T.orange}
                onConfirm={handleClearAll}
                onCancel={() => setClearConfirmOpen(false)}
            />
        </div>
    );
}

/* ═══ View Employee Panel (inline page) ═══ */
function ViewEmployeePanel({ open, employee, onClose, onEdit, onBlock, onDelete, mobile, permissions, permissionsLoading }: any) {
    const [isPinVisible, setIsPinVisible] = useState(false);

    // Reset pin visibility when panel opens with new employee
    useEffect(() => { if (open) setIsPinVisible(false); }, [open]);

    if (!open || !employee) return null;
    const name = employee.fullName?.trim() || `${employee.firstName || ""} ${employee.lastName || ""}`.trim() || "--";
    const initials = `${(employee.firstName || "?")[0]}${(employee.lastName || "")[0] || ""}`.toUpperCase();
    const role = employee.assignedRole || employee.role || "--";
    const outletRaw = employee.locationName || employee.outlet || "";
    const outlet = outletRaw ? (outletRaw.includes(",") ? outletRaw.split(",").pop().trim() : outletRaw) : "--";
    const pin = employee.pin || employee.devicePin || "";
    const phone = employee.phone || employee.mobileNumber || "";
    const isActive = employee.isActive !== undefined ? employee.isActive : (employee.blockUser !== undefined ? !employee.blockUser : true);

    const DetailRow = ({ label, value }) => (
        <div style={{ display: "flex", padding: "10px 0", borderBottom: `1px solid ${T.divider}` }}>
            <div style={{ width: mobile ? 110 : 160, flexShrink: 0, fontSize: 12, fontWeight: 500, color: T.t3 }}>{label}</div>
            <div style={{ fontSize: 13, color: T.t1, flex: 1, wordBreak: "break-word" }}>{value || "--"}</div>
        </div>
    );

    return (
        <div style={{ fontFamily: T.font, animation: "fadeInUp .2s ease" }}>
            <div style={{ padding: mobile ? "16px" : "24px 32px" }}>

                {/* Breadcrumb */}
                <Breadcrumb items={[
                    { label: "Employees", onClick: onClose },
                    { label: "Employee Details" },
                ]} />

                {/* Hero Banner with action buttons */}
                <div style={{ marginBottom: 24 }}>
                    <div style={{
                        background: "linear-gradient(135deg, rgba(229,35,51,0.06) 0%, rgba(229,35,51,0.02) 100%)",
                        border: `1px solid ${T.cardBorder}`, borderRadius: 14,
                        padding: mobile ? 16 : "24px 28px",
                        display: "flex", alignItems: mobile ? "flex-start" : "center",
                        justifyContent: "space-between", position: "relative", overflow: "hidden",
                        flexDirection: mobile ? "column" : "row", gap: mobile ? 16 : 0,
                    }}>
                        {/* Decorative blob */}
                        <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(229,35,51,0.06)", pointerEvents: "none" }} />
                        <div style={{ display: "flex", alignItems: "center", gap: mobile ? 12 : 16, position: "relative", zIndex: 1 }}>
                            <div style={{
                                width: 56, height: 56, borderRadius: 14, flexShrink: 0,
                                background: "linear-gradient(135deg, rgba(229,35,51,0.15), rgba(229,35,51,0.05))",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 20, fontWeight: 700, color: T.accent,
                            }}>{initials}</div>
                            <div>
                                <div style={{ fontSize: mobile ? 18 : 22, fontWeight: 700, color: T.t1, display: "flex", alignItems: "center", gap: 8 }}>
                                    {name}
                                    <StatusBadge isActive={isActive} />
                                </div>
                                <div style={{ fontSize: 13, color: T.t3, marginTop: 2, display: "flex", alignItems: "center", gap: 6 }}>
                                    {role.replace(/_/g, " ")}
                                    {employee.isSuperAdminAccess && <Icon name="bolt" size={14} color={T.accent} />}
                                </div>
                            </div>
                        </div>
                        {/* Action buttons */}
                        <div style={{ display: "flex", gap: 8, position: "relative", zIndex: 1, flexWrap: "wrap" }}>
                            <button onClick={() => onEdit(employee)} style={{
                                display: "flex", alignItems: "center", gap: 6, padding: mobile ? "8px 14px" : "9px 18px", borderRadius: 10,
                                border: `1px solid ${T.cardBorder}`, background: T.surface, color: T.accent,
                                fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.font,
                            }}>
                                <Icon name="edit" size={15} color={T.accent} /> Edit
                            </button>
                            <button onClick={() => onBlock(employee)} style={{
                                display: "flex", alignItems: "center", gap: 6, padding: mobile ? "8px 14px" : "9px 18px", borderRadius: 10,
                                border: `1px solid ${T.cardBorder}`, background: T.surface,
                                color: isActive ? T.orange : T.green,
                                fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.font,
                            }}>
                                <Icon name={isActive ? "block" : "check_circle"} size={15} color={isActive ? T.orange : T.green} />
                                {isActive ? "Block" : "Unblock"}
                            </button>
                            <button onClick={() => onDelete(employee)} style={{
                                display: "flex", alignItems: "center", gap: 6, padding: mobile ? "8px 14px" : "9px 18px", borderRadius: 10,
                                border: `1px solid ${T.cardBorder}`, background: T.surface, color: T.red,
                                fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: T.font,
                            }}>
                                <Icon name="delete" size={15} color={T.red} /> Delete
                            </button>
                        </div>
                    </div>
                </div>

                {/* Details cards — 2-column on desktop, stacked on mobile */}
                <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 20, marginBottom: 24 }}>
                    {/* Personal Info */}
                    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: mobile ? "16px" : "20px 24px", boxShadow: T.cardShadow }}>
                        <SectionHeader icon="person" title="Personal Info" subtitle="Employee personal details" />
                        <DetailRow label="Full Name" value={name} />
                        <DetailRow label="Nick Name" value={employee.nickName?.trim() || "--"} />
                        <DetailRow label="Email" value={employee.email?.trim() || "--"} />
                        <DetailRow label="Phone" value={phone ? phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3") : "--"} />
                        <DetailRow label="Address" value={employee.address?.trim() || "--"} />
                        <DetailRow label="Education" value={employee.education || "--"} />
                        <DetailRow label="Date of Birth" value={formatDateOfBirth(employee.dateOfBirth)} />
                    </div>

                    {/* Formal Setup */}
                    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: mobile ? "16px" : "20px 24px", boxShadow: T.cardShadow }}>
                        <SectionHeader icon="settings" title="Formal Setup" subtitle="Role and access information" />
                        <DetailRow label="User ID" value={employee.userId || "--"} />
                        <DetailRow label="Assigned Role" value={<span style={{ display: "flex", alignItems: "center", gap: 6 }}>{role.replace(/_/g, " ")} {employee.isSuperAdminAccess && <Icon name="bolt" size={14} color={T.accent} />}</span>} />
                        <DetailRow label="Assigned Outlet" value={outlet} />
                        <div style={{ display: "flex", padding: "10px 0", borderBottom: `1px solid ${T.divider}` }}>
                            <div style={{ width: mobile ? 110 : 160, flexShrink: 0, fontSize: 12, fontWeight: 500, color: T.t3 }}>Device Pin</div>
                            <div style={{ fontSize: 13, color: T.t1, flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                                {pin ? (isPinVisible ? pin : "••••") : "--"}
                                {pin && (
                                    <button onClick={() => setIsPinVisible(p => !p)} style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}>
                                        <Icon name={isPinVisible ? "visibility_off" : "visibility"} size={16} color={T.t3} />
                                    </button>
                                )}
                            </div>
                        </div>
                        <DetailRow label="Status" value={<StatusBadge isActive={isActive} />} />
                    </div>
                </div>

                {/* Roles & Functions — hidden for now */}
                <div style={{ display: "none", background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: mobile ? "16px" : "20px 24px", boxShadow: T.cardShadow, marginBottom: 24 }}>
                    <SectionHeader icon="tune" title="Roles & Functions" subtitle="Assigned permissions and access" />
                    {permissionsLoading ? (
                        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
                            {[0, 1, 2].map(i => (
                                <div key={i} style={{ background: T.bg, border: `1px solid ${T.cardBorder}`, borderRadius: 12, overflow: "hidden" }}>
                                    <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.divider}`, display: "flex", alignItems: "center", gap: 10 }}>
                                        <Bone w={18} h={18} r={5} /><Bone w={100} h={13} r={4} style={{ flex: 1 }} /><Bone w={30} h={16} r={8} />
                                    </div>
                                    <div style={{ padding: "8px 12px" }}>
                                        {[0, 1, 2].map(j => (
                                            <div key={j} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 8px" }}>
                                                <Bone w={16} h={16} r={4} /><Bone w="70%" h={12} r={3} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : !permissions || permissions.length === 0 ? (
                        <div style={{ padding: 24, textAlign: "center", color: T.t3, fontSize: 13 }}>
                            <Icon name="info" size={24} color={T.t3} style={{ display: "block", margin: "0 auto 8px" }} />
                            No permissions data available
                        </div>
                    ) : (
                        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
                            {permissions.map(mod => {
                                const funcs = mod.functionality || mod.functions || [];
                                const enabledFuncs = funcs.filter(f => f.isEnabled || f.enabled || f.isAllowed);
                                const allEnabled = funcs.length > 0 && enabledFuncs.length === funcs.length;
                                const someEnabled = enabledFuncs.length > 0;
                                return (
                                    <div key={mod.module || mod.moduleName} style={{
                                        background: T.bg, border: `1px solid ${T.cardBorder}`,
                                        borderRadius: 12, overflow: "hidden",
                                    }}>
                                        {/* Module header */}
                                        <div style={{
                                            padding: "12px 16px", display: "flex", alignItems: "center", gap: 10,
                                            borderBottom: `1px solid ${T.divider}`,
                                            background: allEnabled ? `${T.accent}06` : "transparent",
                                        }}>
                                            <div style={{
                                                width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                                                border: `2px solid ${allEnabled || someEnabled ? T.accent : T.cardBorder}`,
                                                background: allEnabled ? T.accent : "transparent",
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                            }}>
                                                {allEnabled && <Icon name="check" size={12} color="#fff" />}
                                                {someEnabled && !allEnabled && <div style={{ width: 8, height: 2, background: T.accent, borderRadius: 1 }} />}
                                            </div>
                                            <div style={{ flex: 1, fontSize: 13, fontWeight: 700, color: T.t1 }}>{mod.module || mod.moduleName}</div>
                                            <span style={{
                                                fontSize: 10, fontWeight: 600,
                                                color: allEnabled ? T.accent : T.t3,
                                                background: allEnabled ? `${T.accent}12` : T.hover,
                                                padding: "2px 8px", borderRadius: 8,
                                            }}>{enabledFuncs.length}/{funcs.length}</span>
                                        </div>
                                        {/* Functions list */}
                                        <div style={{ padding: "6px 10px" }}>
                                            {funcs.map(func => {
                                                const fname = func.displayName || func.name || func.functionName || "";
                                                const enabled = func.isEnabled || func.enabled || func.isAllowed || false;
                                                return (
                                                    <div key={fname} style={{
                                                        display: "flex", alignItems: "center", gap: 8, padding: "7px 8px",
                                                        borderRadius: 6, marginBottom: 1,
                                                        background: enabled ? `${T.accent}06` : "transparent",
                                                    }}>
                                                        <div style={{
                                                            width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                                                            border: `1.5px solid ${enabled ? T.accent : T.cardBorder}`,
                                                            background: enabled ? T.accent : "transparent",
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                        }}>
                                                            {enabled && <Icon name="check" size={10} color="#fff" />}
                                                        </div>
                                                        <span style={{ fontSize: 12, color: enabled ? T.t1 : T.t3, fontWeight: enabled ? 500 : 400 }}>{fname}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ═══ Edit Employee Page (full-page overlay) ═══ */
function EditEmployeePanel({ open, employee, onClose, onSave, token, locationId, merchantId, businessName, mobile, branches, currentBranchName, actionPerformedByStaffId }: any) {
    const { updateRole } = useAuth();
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<any>({});
    const [showPassword, setShowPassword] = useState(false);
    const [showPin, setShowPin] = useState(true);
    const [pinLoading, setPinLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [checkedFunctions, setCheckedFunctions] = useState([]);
    const [permissionsLoaded, setPermissionsLoaded] = useState(false);
    const [loadedForRole, setLoadedForRole] = useState("");
    const originalPermissionsRef = useRef<string[]>([]);
    const rolesDataRef = useRef([]);
    const dobMaxDate = useMemo(() => getDobMaximumDateIso(), []);

    // Simple direct form state — populated from API response
    const [form, setForm] = useState({
        firstName: "", lastName: "", nickName: "", useNickname: false,
        mobileNumber: "", email: "", address1: "", address2: "",
        education: "", dateOfBirth: "", outlet: "", role: "",
        userId: "", password: "", pin: "",
    });
    // Keep reference to the raw fetched employee for payload building
    const [fetchedEmp, setFetchedEmp] = useState<any>(null);
    const editEmp = fetchedEmp || employee || {};

    const set = (key: any, val: any) => setForm(prev => ({ ...prev, [key]: val }));

    // Build userAccessInfoList in the portal format: [{ moduleType, moduleName, urls }]
    const buildAccessInfoList = () => {
        const roles = rolesDataRef.current;
        if (!roles || roles.length === 0) return [];
        return roles
            .map(mod => {
                const moduleFuncs = (mod.functionality || [])
                    .filter(f => f.displayName && checkedFunctions.includes(f.displayName.toLowerCase()))
                    .map(f => ({
                        moduleType: mod.module,
                        moduleName: f.name,
                        urls: f.urls || [],
                    }));
                return moduleFuncs.length > 0 ? moduleFuncs : null;
            })
            .flat()
            .filter(Boolean);
    };

    // Helper: populate form fields from an employee object
    const populateForm = (emp: any) => {
        if (!emp || typeof emp !== "object") return;
        const addrRaw = emp.address || "";
        let a1 = addrRaw, a2 = "";
        if (addrRaw.includes(",")) {
            const parts = addrRaw.split(",");
            a2 = parts.pop().trim();
            a1 = parts.join(",").trim();
        }
        const newForm = {
            firstName: emp.firstName?.trim() || "",
            lastName: emp.lastName?.trim() || "",
            nickName: emp.nickName?.trim() || "",
            useNickname: emp.toUseNickName || emp.isToUseNickName || false,
            mobileNumber: emp.phone || emp.mobileNumber || "",
            email: emp.email || "",
            address1: a1,
            address2: a2,
            education: emp.education || "",
            dateOfBirth: normalizeDateOfBirth(emp.dateOfBirth),
            outlet: emp.locationName || emp.outlet || currentBranchName || "",
            role: emp.assignedRole || emp.role || "",
            userId: emp.userId || emp.user_id || "",
            password: "",
            pin: emp.pin || emp.devicePin || "",
        };
        setForm(newForm);
    };

    // Fetch full employee details from /merchants/staff/details
    useEffect(() => {
        if (!open || !employee?.staffId || !merchantId || !token) {
            return;
        }
        let cancelled = false;
        const key = employee.staffId;
        setErrors({});
        setShowPassword(false);
        setLoading(true);
        setFetchedEmp(null);

        if (!employeeDetailInFlight.has(key)) {
            employeeDetailInFlight.set(key, getEmployeeById({ staffId: key, merchantId, token }));
        }
        employeeDetailInFlight.get(key)!.then(res => {
            employeeDetailInFlight.delete(key);
            if (cancelled) return;
            // Extract employee from all possible response shapes
            let emp = null;
            if (Array.isArray(res)) {
                emp = res[0];
            } else if (res && typeof res === "object") {
                if (res.staffId || res.userId || res.firstName) {
                    emp = res;
                } else if (res.metaDataInfo?.data) {
                    const d = res.metaDataInfo.data;
                    emp = Array.isArray(d) ? d[0] : d;
                } else if (res.data) {
                    const d = res.data;
                    emp = Array.isArray(d) ? d[0] : d;
                } else if (Array.isArray(res.content)) {
                    emp = res.content[0];
                } else {
                    emp = res;
                }
            }

            if (emp && typeof emp === "object" && !Array.isArray(emp)) {
                setFetchedEmp(emp);
                populateForm(emp);
            } else {
                setFetchedEmp(employee);
                populateForm(employee);
            }
        }).catch(() => {
            employeeDetailInFlight.delete(key);
            if (cancelled) return;
            setFetchedEmp(employee);
            populateForm(employee);
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, employee?.staffId, merchantId, token]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch existing staff permissions for edit pre-population
    useEffect(() => {
        if (!open || !employee?.staffId || !merchantId || !token) return;
        let cancelled = false;
        const key = employee.staffId;
        setPermissionsLoaded(false);
        setLoadedForRole("");
        originalPermissionsRef.current = [];
        if (!staffPermissionsInFlight.has(key)) {
            staffPermissionsInFlight.set(key, getStaffPermissions({ staffId: key, merchantId, token }));
        }
        staffPermissionsInFlight.get(key)!.then(res => {
            staffPermissionsInFlight.delete(key);
            if (cancelled) return;
            // Response: [{module, funtions: ["Func1", ...]}] — note API typo "funtions"
            const list = Array.isArray(res) ? res
                : Array.isArray(res?.metaDataInfo?.data) ? res.metaDataInfo.data
                : Array.isArray(res?.data) ? res.data
                : [];
            if (list.length > 0) {
                const names = list.flatMap((m: any) => (m.funtions || m.functions || []) as string[])
                    .map((f: string) => f.toLowerCase())
                    .filter(Boolean);
                const empRole = employee?.assignedRole || employee?.role || "";
                originalPermissionsRef.current = names;
                setCheckedFunctions(names);
                setLoadedForRole(empRole);
                setPermissionsLoaded(true);
            }
        }).catch(() => { staffPermissionsInFlight.delete(key); });
        return () => { cancelled = true; };
    }, [open, employee?.staffId, merchantId, token]); // eslint-disable-line react-hooks/exhaustive-deps

    // When role changes back to the originally loaded role, restore API permissions
    useEffect(() => {
        if (!permissionsLoaded || !loadedForRole || !form.role) return;
        if (form.role === loadedForRole) {
            setCheckedFunctions([...originalPermissionsRef.current]);
        }
    }, [form.role]); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshPin = () => {
        if (!merchantId || !token) return;
        setPinLoading(true);
        getDevicePin({ merchantId, token }).then(res => {
            const pin = res?.devicePin ?? res?.metaDataInfo?.data ?? res?.data ?? res?.pin ?? res;
            if (pin != null) set("pin", String(pin).padStart(4, "0"));
        }).catch(() => {}).finally(() => setPinLoading(false));
    };

    const validate = () => {
        const e: any = {};
        const firstName = String(form.firstName ?? "").trim();
        const lastName = String(form.lastName ?? "").trim();
        const nickName = String(form.nickName ?? "").trim();
        const education = String(form.education ?? "").trim();
        const address1 = String(form.address1 ?? "").trim();
        const address2 = String(form.address2 ?? "").trim();
        const phoneDigits = String(form.mobileNumber ?? "").replace(/\D/g, "");
        if (!firstName || firstName.length < 2) e.firstName = "Min 2 characters, letters only";
        if (firstName && !isLettersOnly(firstName)) e.firstName = "Letters only";
        if (lastName && !isLettersOnly(lastName)) e.lastName = "Letters only";
        if (nickName && isOnlySpecialCharacters(nickName)) e.nickName = onlySpecialCharacterTextError;
        if (education && isOnlySpecialCharacters(education)) e.education = onlySpecialCharacterTextError;
        if (address1 && isOnlySpecialCharacters(address1)) e.address1 = onlySpecialCharacterTextError;
        if (address2 && isOnlySpecialCharacters(address2)) e.address2 = onlySpecialCharacterTextError;
        if (phoneDigits.length > 0 && phoneDigits.length < 10) e.mobileNumber = "Phone number must be at least 10 digits";
        if (!form.role) e.role = "Role is required";
        if (form.password && form.password.length < 8) e.password = "Min 8 chars, 1 uppercase, 1 number, 1 special";
        if (form.password && !/^(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/.test(form.password)) e.password = "Min 8 chars, 1 uppercase, 1 number, 1 special";
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
        if (normalizeDateOfBirth(form.dateOfBirth) && !isDob18OrAbove(form.dateOfBirth)) e.dateOfBirth = "Employee must be 18 years or older";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const address = [form.address1.trim(), form.address2.trim()].filter(Boolean).join(" ").trim() || null;
            const accessInfoList = buildAccessInfoList();
            const resolvedLocationId = (branches || []).find(b =>
                (b.locationName || "").includes(form.outlet)
            )?.id || locationId;
            const payload = {
                id: employee.staffId,
                firstName: form.firstName.trim(),
                lastName: (form.lastName || "").trim(),
                fullName: `${form.firstName.trim()} ${(form.lastName || "").trim()}`.trim(),
                nickName: form.nickName.trim(),
                isToUseNickName: form.useNickname,
                role: form.role,
                businessName,
                userId: form.userId.trim() || editEmp?.userId || "",
                password: form.password || null,
                email: form.email.trim() || null,
                mobileNumber: form.mobileNumber.replace(/-/g, "") || "",
                address,
                education: form.education.trim() || null,
                dateOfBirth: normalizeDateOfBirth(form.dateOfBirth) || null,
                devicePin: form.pin || null,
                merchantId,
                locationId: resolvedLocationId,
                IsTempPassword: false,
                isDefaultFunctionalityAccessUpdated: accessInfoList.length > 0,
                userAccessInfoList: accessInfoList,
            };
            await updateEmployeeApi({ details: payload, token, actionPerformedByStaffId });
            if (locationId) employeeListInFlight.delete(locationId);
            // If the edited record belongs to the signed-in user, patch their live session role so
            // route/nav access (e.g. Employee section, Offers, Loyalty) reflects it immediately.
            if (employee.staffId && actionPerformedByStaffId && employee.staffId === actionPerformedByStaffId) {
                await updateRole(form.role);
            }
            await onSave?.();
            onClose();
        } catch (err) {
            setErrors({ submit: err.message || "Failed to update employee" });
        } finally { setSaving(false); }
    };

    if (!open) return null;

    const empName = editEmp.firstName ? `${editEmp.firstName || ""} ${editEmp.lastName || ""}`.trim() : "";

    return (
        <div style={{ fontFamily: T.font, animation: "fadeInUp .2s ease" }}>
            {loading ? (
                /* ── Edit form shimmer — real labels/headers, shimmer only on values ── */
                <div style={{ padding: mobile ? "16px" : "24px 32px" }}>
                    {/* Breadcrumb */}
                    <Breadcrumb items={[{ label: "Employees" }, { label: "Edit Employee" }]} />

                    {/* Hero Banner */}
                    <div style={{ marginBottom: 24 }}>
                        <HeroBanner icon="edit" title="Edit Employee" subtitle="Update employee information" mobile={mobile} />
                    </div>

                    {/* Personal Info card — real labels, shimmer inputs */}
                    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: mobile ? 16 : "24px 28px", boxShadow: T.cardShadow, marginBottom: 20 }}>
                        <SectionHeader icon="person" title="Personal Information" subtitle="Employee personal details" />
                        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: "16px 20px" }}>
                            {["First Name *", "Last Name", "Nick Name", "Mobile Number", "Email", "Date of Birth"].map((label, i) => (
                                <div key={i}>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, marginBottom: 6 }}>{label}</div>
                                    <Bone h={38} r={8} />
                                </div>
                            ))}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: "16px 20px", marginTop: 16 }}>
                            {["Address Line 1", "Address Line 2"].map((label, i) => (
                                <div key={i}>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, marginBottom: 6 }}>{label}</div>
                                    <Bone h={38} r={8} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Formal Setup card — real labels, shimmer inputs */}
                    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: mobile ? 16 : "24px 28px", boxShadow: T.cardShadow, marginBottom: 20 }}>
                        <SectionHeader icon="settings" title="Formal Setup" subtitle="Role and access information" />
                        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: "16px 20px" }}>
                            {["Assigned Outlet", "Assigned Role *", "User ID"].map((label, i) => (
                                <div key={i}>
                                    <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, marginBottom: 6 }}>{label}</div>
                                    <Bone h={38} r={8} />
                                </div>
                            ))}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: "16px 20px", marginTop: 16 }}>
                            <div>
                                <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, marginBottom: 6 }}>Password</div>
                                <Bone h={38} r={8} />
                            </div>
                            <div>
                                <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, marginBottom: 6 }}>Device Pin</div>
                                <Bone h={38} r={8} />
                            </div>
                        </div>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                        <button disabled style={{ padding: "10px 24px", borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: T.surface, color: T.t3, fontSize: 13, fontWeight: 600, fontFamily: T.font }}>Cancel</button>
                        <button disabled style={{ padding: "10px 28px", borderRadius: 10, border: "none", background: T.accent, color: "#fff", fontSize: 13, fontWeight: 600, fontFamily: T.font, opacity: 0.5 }}>Save Changes</button>
                    </div>
                </div>
            ) : (
                /* Form body — renders inline within layout */
                <div style={{ padding: mobile ? "16px" : "24px 32px" }}>

                    {/* Breadcrumb */}
                    <Breadcrumb items={[
                        { label: "Employees", onClick: onClose },
                        { label: "Edit Employee" },
                    ]} />

                    {/* Hero Banner */}
                    <div style={{ marginBottom: 24 }}>
                        <HeroBanner
                            icon="edit"
                            title="Edit Employee"
                            subtitle={empName || "Update employee details"}
                            mobile={mobile}
                        />
                    </div>

                    {/* ── Personal Info ── */}
                    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: mobile ? "16px" : "24px 28px", boxShadow: T.cardShadow, marginBottom: 20 }}>
                        <SectionHeader icon="person" title="Personal Information" subtitle="Basic details about the employee" />

                        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: "0 20px" }}>
                            <FormField label="First Name" required error={errors.firstName}>
                                <input style={inputStyle} value={form.firstName} onChange={e => set("firstName", sanitizeLettersOnlyInput(e.target.value))} placeholder="First name" />
                            </FormField>
                            <FormField label="Last Name" error={errors.lastName}>
                                <input style={inputStyle} value={form.lastName} onChange={e => set("lastName", sanitizeLettersOnlyInput(e.target.value))} placeholder="Last name" />
                            </FormField>
                            <FormField label="Nick Name" error={errors.nickName}>
                                <input style={inputStyle} value={form.nickName} onChange={e => set("nickName", e.target.value)} placeholder="Nick name" />
                            </FormField>
                        </div>

                        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, cursor: "pointer", fontSize: 12, color: T.t2 }}>
                            <input type="checkbox" checked={form.useNickname} onChange={e => set("useNickname", e.target.checked)}
                                style={{ accentColor: T.accent, width: 16, height: 16 }} />
                            Utilize nickname in all forthcoming activities
                        </label>

                        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: "0 20px" }}>
                            <FormField label="Phone" error={errors.mobileNumber}>
                                <input style={inputStyle} type="tel" value={form.mobileNumber}
                                    onChange={e => {
                                        const formatted = formatPhone(e.target.value);
                                        const digits = formatted.replace(/\D/g, "");
                                        set("mobileNumber", formatted);
                                        setErrors(prev => ({
                                            ...prev,
                                            mobileNumber: digits.length > 0 && digits.length < 10 ? "Phone number must be at least 10 digits" : undefined,
                                        }));
                                    }}
                                    placeholder="999-999-9999" maxLength={12} />
                            </FormField>
                            <FormField label="Email" error={errors.email}>
                                <input style={inputStyle} type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="Email address" />
                            </FormField>
                            <FormField label="Education" error={errors.education}>
                                <input style={inputStyle} value={form.education} onChange={e => set("education", e.target.value)} placeholder="Education" />
                            </FormField>
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: "0 20px" }}>
                            <FormField label="Address Line 1" error={errors.address1}>
                                <input style={inputStyle} value={form.address1} onChange={e => set("address1", e.target.value)} placeholder="Address line 1" />
                            </FormField>
                            <FormField label="Address Line 2" error={errors.address2}>
                                <input style={inputStyle} value={form.address2} onChange={e => set("address2", e.target.value)} placeholder="Address line 2" />
                            </FormField>
                            <FormField label="Date of Birth" error={errors.dateOfBirth}>
                                <input style={inputStyle} type="date" value={normalizeDateOfBirth(form.dateOfBirth)}
                                    onChange={e => set("dateOfBirth", normalizeDateOfBirth(e.target.value))}
                                    min={DOB_MIN_DATE_ISO} max={dobMaxDate} />
                            </FormField>
                        </div>
                    </div>

                    {/* ── Formal Setup ── */}
                    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: mobile ? "16px" : "24px 28px", boxShadow: T.cardShadow, marginBottom: 20 }}>
                        <SectionHeader icon="settings" title="Formal Setup" subtitle="Role, access credentials and permissions" />

                        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: "0 20px" }}>
                            <FormField label="Outlet">
                                <select style={{ ...inputStyle, cursor: "default", background: T.hover, color: T.t3 }} value={form.outlet} disabled>
                                    <option value="">{form.outlet || "Select outlet"}</option>
                                    {form.outlet && <option value={form.outlet}>{form.outlet}</option>}
                                </select>
                            </FormField>
                            <FormField label="Roles" required error={errors.role}>
                                <select style={{ ...inputStyle, cursor: "pointer" }} value={form.role} onChange={e => set("role", e.target.value)}>
                                    <option value="">Select role</option>
                                    {RESTAURANT_ROLES.map(r => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                                </select>
                            </FormField>
                            <FormField label="User ID">
                                <input style={{ ...inputStyle, background: T.hover, color: T.t1 }} value={form.userId} disabled readOnly />
                            </FormField>
                        </div>
                        <RolesFunctionsEditor token={token} selectedRole={form.role} checkedFunctions={checkedFunctions} setCheckedFunctions={setCheckedFunctions} rolesDataRef={rolesDataRef} skipAutoPopulate={permissionsLoaded && form.role === loadedForRole} />

                        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: "0 20px" }}>
                            <FormField label="Password" error={errors.password}>
                                <div style={{ position: "relative" }}>
                                    <input className="employee-password-input" style={{ ...inputStyle, paddingRight: 40 }} type={showPassword ? "text" : "password"}
                                        value={form.password} onChange={e => {
                                            const val = e.target.value;
                                            set("password", val);
                                            const invalid = val.length > 0 && (val.length < 8 || !/^(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/.test(val));
                                            setErrors(prev => ({ ...prev, password: invalid ? "Min 8 chars, 1 uppercase, 1 number, 1 special" : undefined }));
                                        }}
                                        placeholder="Leave empty to keep current" autoComplete="new-password" />
                                    <button type="button" onClick={() => setShowPassword(p => !p)} style={{
                                        position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                                        background: "none", border: "none", cursor: "pointer", padding: 4,
                                    }}>
                                        <Icon name={showPassword ? "visibility" : "visibility_off"} size={16} color={T.t3} />
                                    </button>
                                </div>
                            </FormField>
                            <FormField label="Create Pin">
                                <div className="employee-pin-row">
                                    <div className="employee-pin-digits">
                                        {[0, 1, 2, 3].map(idx => (
                                            <input key={idx} style={{
                                                ...inputStyle, width: 44, textAlign: "center", fontSize: 18, fontWeight: 700,
                                                letterSpacing: 0, padding: "10px 0",
                                            }} type={showPin ? "text" : "password"} value={form.pin[idx] || ""} disabled placeholder="-" readOnly />
                                        ))}
                                    </div>
                                    <div className="employee-pin-actions">
                                        <button type="button" onClick={() => setShowPin(p => !p)} className="employee-pin-icon-btn">
                                            <Icon name={showPin ? "visibility" : "visibility_off"} size={16} color={T.t2} />
                                        </button>
                                        <button type="button" onClick={refreshPin} disabled={pinLoading} className="employee-pin-icon-btn">
                                            <span className={pinLoading ? "employee-pin-spin" : ""}>
                                                <Icon name="refresh" size={16} color={T.t2} />
                                            </span>
                                        </button>
                                    </div>
                                </div>
                                <div style={{ fontSize: 10, color: T.t3, marginTop: 4 }}>The four digit pin will be used for validating the user</div>
                            </FormField>
                        </div>
                    </div>

                    {errors.submit && (
                        <div style={{ padding: "12px 16px", borderRadius: 12, background: T.redSoft, color: T.red, fontSize: 13, fontWeight: 600, marginBottom: 20 }}>
                            {errors.submit}
                        </div>
                    )}

                    {/* Bottom action buttons */}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingBottom: 24 }}>
                        <button type="button" onClick={onClose} disabled={saving} style={{
                            padding: "10px 22px", borderRadius: 10, border: `1px solid ${T.cardBorder}`,
                            background: T.surface, color: T.t2, fontSize: 13, fontWeight: 600,
                            cursor: "pointer", fontFamily: T.font, display: "flex", alignItems: "center", gap: 6,
                        }}>
                            <Icon name="close" size={16} color={T.t2} /> Cancel
                        </button>
                        <button type="button" onClick={handleSave} disabled={saving} style={{
                            padding: "10px 24px", borderRadius: 10, border: "none",
                            background: T.accent, color: "#fff", fontSize: 13, fontWeight: 600,
                            cursor: "pointer", fontFamily: T.font, display: "flex", alignItems: "center", gap: 6,
                            opacity: saving ? 0.6 : 1,
                        }}>
                            <Icon name="save" size={16} color="#fff" />
                            {saving ? "Saving..." : "Update Employee"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ═══ Table Headers ═══ */
const HEADERS = [
    { key: "name", label: "Name", width: "1.6fr", sortable: true },
    { key: "role", label: "Role", width: "1.4fr", sortable: true },
    { key: "status", label: "Status", width: "1fr", sortable: true },
    { key: "edit", label: "Edit", width: "0.6fr", sortable: false },
    { key: "block", label: "Un/Block", width: "0.7fr", sortable: false },
    { key: "delete", label: "Delete", width: "0.6fr", sortable: false },
    { key: "view", label: "View", width: "0.5fr", sortable: false },
];

function EmployeeEmptyState({ noAccess, fetchError, onAdd, onRetry }: any) {
    // Single empty-state renderer used by both mobile and desktop list views.
    const hasFetchError = Boolean(fetchError);
    const iconName = noAccess ? "lock" : hasFetchError ? "wifi_off" : "person_off";
    const title = noAccess
        ? "You don't have access to employees"
        : hasFetchError
            ? "Unable to load employees"
            : "No employees found";
    const subtitle = noAccess
        ? "Contact your administrator for access"
        : hasFetchError
            ? fetchError
            : "Try adjusting your search or filters";

    return (
        <div style={{ textAlign: "center", padding: 56, color: T.t3 }}>
            <Icon name={iconName} size={48} color={T.t3} style={{ display: "block", margin: "0 auto 12px" }} />
            <div style={{ fontSize: 16, fontWeight: 700, color: T.t1, marginBottom: 4 }}>{title}</div>
            <div style={{ fontSize: 13, color: T.t3, marginBottom: 16 }}>{subtitle}</div>
            {hasFetchError ? (
                <button onClick={onRetry} style={{
                    display: "flex", alignItems: "center", gap: 6, margin: "0 auto",
                    padding: "10px 20px", borderRadius: 10, border: "none",
                    background: T.accent, color: "#fff", fontSize: 13, fontWeight: 600,
                    cursor: "pointer", fontFamily: T.font,
                }}>
                    <Icon name="refresh" size={16} color="#fff" />
                    Retry
                </button>
            ) : (
                <button disabled={noAccess} onClick={() => !noAccess && onAdd()} style={{
                    display: "flex", alignItems: "center", gap: 6, margin: "0 auto",
                    padding: "10px 20px", borderRadius: 10, border: "none",
                    background: noAccess ? T.t3 : T.accent, color: "#fff", fontSize: 13, fontWeight: 600,
                    cursor: noAccess ? "not-allowed" : "pointer", fontFamily: T.font,
                    opacity: noAccess ? 0.5 : 1,
                }}>
                    <Icon name="person_add" size={16} color="#fff" />
                    + Add Employee
                </button>
            )}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN CONTENT (no date filters)
   ═══════════════════════════════════════════════════════════════════════════ */
function EmployeeContent({ mobile }: any) {
    const { employees, branches, fetchEmployees, token, locationId, merchantId, businessName, currentBranchName, loading, noAccess, loggedInStaffId, fetchError } = useEmployeeCtx();
    const navigate = useNavigate();
    const searchRef = useRef<any>(null);

    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("");
    const [sortAsc, setSortAsc] = useState(true);
    const [statusTab, setStatusTab] = useState("all");
    const [modal, setModal] = useState({ open: false, type: "", employee: null });
    const [actionLoading, setActionLoading] = useState(false);
    const [actionMenuId, setActionMenuId] = useState<string | null>(null);

    // Filter by status tab
    const tabFiltered = useMemo(() => {
        if (statusTab === "active") return employees.filter(e => e.isActive);
        if (statusTab === "blocked") return employees.filter(e => !e.isActive);
        return employees;
    }, [employees, statusTab]);

    // Search
    const searched = useMemo(() => {
        if (!search.trim()) return tabFiltered;
        const q = search.toLowerCase().trim().replace(/\s+/g, "");
        return tabFiltered.filter(e => {
            const name = `${e.firstName || ""}${e.lastName || ""}`.toLowerCase();
            const role = (e.role || "").toLowerCase();
            return name.includes(q) || role.includes(q);
        });
    }, [tabFiltered, search]);

    // Sort
    const sorted = useMemo(() => {
        if (!sortBy) return searched;
        return [...searched].sort((a, b) => {
            let aV, bV;
            if (sortBy === "name") { aV = `${a.firstName || ""} ${a.lastName || ""}`.toLowerCase(); bV = `${b.firstName || ""} ${b.lastName || ""}`.toLowerCase(); }
            else if (sortBy === "role") { aV = (a.role || "").toLowerCase(); bV = (b.role || "").toLowerCase(); }
            else if (sortBy === "status") { aV = a.isActive ? "a" : "b"; bV = b.isActive ? "a" : "b"; }
            else return 0;
            return sortAsc ? aV.localeCompare(bV) : bV.localeCompare(aV);
        });
    }, [searched, sortBy, sortAsc]);

    const handleSort = useCallback(col => {
        if (sortBy === col) setSortAsc(p => !p);
        else { setSortBy(col); setSortAsc(true); }
    }, [sortBy]);

    const closeModal = () => setModal({ open: false, type: "", employee: null });

    const handleBlockUnblock = async () => {
        const emp = modal.employee; if (!emp) return;
        setActionLoading(true);
        try { await updateEmployeeStatus({ staffId: emp.staffId, isToBlock: emp.isActive, token, actionPerformedByStaffId: loggedInStaffId }); await fetchEmployees(); } catch { /* */ }
        setActionLoading(false); closeModal();
    };
    const handleDelete = async () => {
        const emp = modal.employee; if (!emp) return;
        setActionLoading(true);
        try { await deleteEmployeeApi({ staffId: emp.staffId, token, actionPerformedByStaffId: loggedInStaffId }); await fetchEmployees(); } catch { /* */ }
        setActionLoading(false); closeModal();
    };

    const totalCount = employees.length;
    const activeCount = employees.filter(e => e.isActive).length;
    const blockedCount = employees.filter(e => !e.isActive).length;

    if (!locationId) return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: T.t3, padding: 24, fontSize: 13 }}>Select a location to view Employees.</div>;
    if (loading) return mobile ? <MobileShimmer /> : <DesktopShimmer />;

    const tabs = [
        { id: "all", label: "All", count: totalCount },
        { id: "active", label: "Active", count: activeCount },
        { id: "blocked", label: "Blocked", count: blockedCount },
    ];

    const gridCols = HEADERS.map(h => h.width).join(" ");

    /* ── MOBILE ── */
    if (mobile) {
        return (
            <div style={{ padding: "12px 16px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                {/* Hero Banner */}
                <HeroBanner
                    icon="group"
                    title="Employees"
                    subtitle={`${totalCount} total, ${activeCount} active`}
                    mobile
                />

                {/* Add New + Search */}
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <div style={{ position: "relative", flex: 1 }}>
                        <Icon name="search" size={16} color={T.t3} style={{ position: "absolute", left: 12, top: 10 }} />
                        <input ref={searchRef} type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employees..."
                            style={{ width: "100%", border: `1px solid ${T.cardBorder}`, borderRadius: 24, padding: "9px 12px 9px 34px", fontSize: 12, fontFamily: T.font, color: T.t1, outline: "none", background: T.surface, boxSizing: "border-box" }} />
                    </div>
                    <button disabled={noAccess} onClick={() => !noAccess && navigate("/employees/add")} style={{
                        display: "flex", alignItems: "center", gap: 4, padding: "9px 14px", borderRadius: 12,
                        border: "none", background: noAccess ? T.t3 : T.accent, color: "#fff", fontSize: 12, fontWeight: 600,
                        cursor: noAccess ? "not-allowed" : "pointer", fontFamily: T.font, whiteSpace: "nowrap", flexShrink: 0,
                        opacity: noAccess ? 0.5 : 1,
                    }}>
                        <Icon name="add" size={16} color="#fff" /> Add
                    </button>
                </div>
                {/* Status tabs (pills for mobile) */}
                <div style={{ display: "flex", gap: 6 }}>
                    {tabs.map(t => (
                        <button key={t.id} onClick={() => setStatusTab(t.id)} style={{
                            flex: 1, padding: "7px 0", borderRadius: 10, fontSize: 11, fontWeight: 600,
                            border: statusTab === t.id ? `1.5px solid ${T.accent}` : `1px solid ${T.cardBorder}`,
                            background: statusTab === t.id ? T.accentSoft : T.surface,
                            color: statusTab === t.id ? T.accent : T.t2,
                            cursor: "pointer", fontFamily: T.font, transition: "all .15s",
                        }}>{t.label} ({t.count})</button>
                    ))}
                </div>

                {/* Cards */}
                {sorted.length === 0 ? (
                    <EmployeeEmptyState
                        noAccess={noAccess}
                        fetchError={fetchError}
                        onAdd={() => navigate("/employees/add")}
                        onRetry={fetchEmployees}
                    />
                ) : sorted.map(emp => {
                    const name = `${emp.firstName || ""} ${emp.lastName || ""}`.trim();
                    const initials = `${(emp.firstName || "?")[0]}${(emp.lastName || "")[0] || ""}`.toUpperCase();
                    return (
                        <div key={emp.staffId} style={{
                            background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14,
                            padding: "14px 16px", boxShadow: T.shadow, position: "relative",
                        }}>
                            <div
                                onClick={() => navigate(`/employees/view/${emp.staffId}`)}
                                style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}
                            >
                                <div style={{
                                    width: 38, height: 38, borderRadius: 12, flexShrink: 0,
                                    background: "linear-gradient(135deg, rgba(229,35,51,0.12), rgba(229,35,51,0.04))",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 13, fontWeight: 700, color: T.accent,
                                }}>{initials}</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: T.t1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                                    <div style={{ fontSize: 11, color: T.t3, marginTop: 1 }}>{emp.role || "--"}</div>
                                </div>
                                <StatusBadge isActive={emp.isActive} />
                                <span
                                    onClick={(e) => { e.stopPropagation(); setActionMenuId(actionMenuId === emp.staffId ? null : emp.staffId); }}
                                    className="material-symbols-rounded"
                                    style={{ fontSize: 20, color: T.t3, cursor: "pointer", padding: 4, marginLeft: 4 }}
                                >more_vert</span>
                            </div>
                            {/* 3-dot action menu */}
                            {actionMenuId === emp.staffId && (
                                <>
                                    <div onClick={() => setActionMenuId(null)} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} />
                                    <div style={{
                                        position: "absolute", top: 48, right: 12, zIndex: 999,
                                        background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 10,
                                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)", overflow: "hidden", minWidth: 140,
                                    }}>
                                        <button onClick={() => { setActionMenuId(null); navigate(`/employees/edit/${emp.staffId}`); }} style={{
                                            display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "11px 16px",
                                            fontSize: 13, fontWeight: 500, color: T.t1, background: "none", border: "none",
                                            cursor: "pointer", fontFamily: T.font, textAlign: "left",
                                        }}>
                                            <Icon name="edit" size={16} color={T.t2} /> Edit
                                        </button>
                                        <button onClick={() => { setActionMenuId(null); setModal({ open: true, type: "block", employee: emp }); }} style={{
                                            display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "11px 16px",
                                            fontSize: 13, fontWeight: 500, color: emp.isActive ? T.orange : T.green,
                                            background: "none", border: "none", cursor: "pointer", fontFamily: T.font, textAlign: "left",
                                        }}>
                                            <Icon name={emp.isActive ? "block" : "check_circle"} size={16} color={emp.isActive ? T.orange : T.green} />
                                            {emp.isActive ? "Block" : "Unblock"}
                                        </button>
                                        <button onClick={() => { setActionMenuId(null); setModal({ open: true, type: "delete", employee: emp }); }} style={{
                                            display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "11px 16px",
                                            fontSize: 13, fontWeight: 500, color: T.red, background: "none", border: "none",
                                            cursor: "pointer", fontFamily: T.font, textAlign: "left",
                                        }}>
                                            <Icon name="delete" size={16} color={T.red} /> Delete
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}

                <ConfirmModal open={modal.open && modal.type === "block"} icon={modal.employee?.isActive ? "block" : "check_circle"}
                    iconColor={modal.employee?.isActive ? T.orange : T.green}
                    title={modal.employee?.isActive ? "Block Employee?" : "Unblock Employee?"}
                    message={modal.employee?.isActive ? "This will restrict their access until unblocked." : "This will restore their access."}
                    confirmLabel={modal.employee?.isActive ? "Block" : "Unblock"} confirmColor={modal.employee?.isActive ? T.orange : T.green}
                    onConfirm={handleBlockUnblock} onCancel={closeModal} loading={actionLoading} />
                <ConfirmModal open={modal.open && modal.type === "delete"} icon="delete" iconColor={T.red}
                    title="Delete Employee?" message="This action is irreversible. All data will be permanently removed."
                    confirmLabel="Delete" confirmColor={T.red} onConfirm={handleDelete} onCancel={closeModal} loading={actionLoading} />
            </div>
        );
    }

    /* ── DESKTOP ── */
    return (
        <div style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Hero Banner */}
            <HeroBanner
                icon="group"
                title="Employees"
                subtitle={noAccess ? "You don't have access to manage employees" : `Manage your team — ${totalCount} members, ${activeCount} active`}
                ctaLabel="+ Add New"
                ctaIcon="person_add"
                onCta={() => navigate("/employees/add")}
                ctaDisabled={noAccess}
            />

            {/* Search Bar */}
            <div style={{ position: "relative" }}>
                <Icon name="search" size={18} color={T.t3} style={{ position: "absolute", left: 16, top: 13 }} />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employees..."
                    style={{
                        width: "100%", border: `1px solid ${T.cardBorder}`, borderRadius: 24,
                        padding: "12px 18px 12px 44px", fontSize: 13, fontFamily: T.font,
                        color: T.t1, outline: "none", background: T.surface, boxSizing: "border-box",
                        transition: "border .15s",
                    }} />
            </div>

            {/* Underline Tabs */}
            <div>
                <div style={{ display: "flex", gap: 0 }}>
                    {tabs.map(t => (
                        <button key={t.id} onClick={() => setStatusTab(t.id)} style={{
                            padding: "10px 20px", fontSize: 13, border: "none", background: "none",
                            cursor: "pointer", fontFamily: T.font, transition: "all .15s",
                            fontWeight: statusTab === t.id ? 600 : 400,
                            color: statusTab === t.id ? T.accent : T.t2,
                            borderBottom: statusTab === t.id ? `2px solid ${T.accent}` : "2px solid transparent",
                        }}>{t.label} ({t.count})</button>
                    ))}
                </div>
                <div style={{ borderBottom: `1px solid ${T.divider}` }} />
            </div>

            {/* Table Card */}
            <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, boxShadow: T.cardShadow, overflow: "hidden" }}>
                {/* Column headers */}
                <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 8, padding: "10px 14px", background: T.bg }}>
                    {HEADERS.map(h => (
                        <div key={h.key} onClick={h.sortable ? () => handleSort(h.key) : undefined} style={{
                            fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase",
                            letterSpacing: 0.5, cursor: h.sortable ? "pointer" : "default",
                            display: "flex", alignItems: "center", gap: 3, userSelect: "none",
                        }}>
                            {h.label}
                            {h.sortable && <span style={{ fontSize: 11, color: sortBy === h.key ? T.accent : T.t3, opacity: sortBy === h.key ? 1 : 0.35 }}>{sortBy === h.key ? (sortAsc ? "\u2191" : "\u2193") : "\u21C5"}</span>}
                        </div>
                    ))}
                </div>

                {/* Rows */}
                {sorted.length === 0 ? (
                    <EmployeeEmptyState
                        noAccess={noAccess}
                        fetchError={fetchError}
                        onAdd={() => navigate("/employees/add")}
                        onRetry={fetchEmployees}
                    />
                ) : sorted.map((emp, i) => {
                    const name = `${emp.firstName || ""} ${emp.lastName || ""}`.trim();
                    const roleLabel = emp.role ? emp.role.replace(/_/g, " ") : "--";
                    const hasSuperAdmin = emp.isSuperAdminAccess;
                    return (
                        <div key={emp.staffId} style={{
                            display: "grid", gridTemplateColumns: gridCols, gap: 8,
                            padding: "12px 14px", borderBottom: `1px solid ${T.divider}`, alignItems: "center",
                            transition: "background .1s",
                        }} onMouseEnter={e => e.currentTarget.style.background = T.hover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            {/* Name */}
                            <div style={{ fontSize: 12, fontWeight: 600, color: T.t1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                            {/* Role + super admin bolt */}
                            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: T.t1 }}>
                                {roleLabel}
                                {hasSuperAdmin && <Icon name="bolt" size={14} color={T.accent} />}
                            </div>
                            {/* Status */}
                            <div><StatusBadge isActive={emp.isActive} /></div>
                            {/* Edit */}
                            <div>
                                <button onClick={() => navigate(`/employees/edit/${emp.staffId}`)} style={{
                                    background: "none", border: "none", cursor: "pointer", padding: 0,
                                    fontSize: 12, fontWeight: 600, color: T.accent, fontFamily: T.font,
                                }}>Edit</button>
                            </div>
                            {/* Un/Block */}
                            <div>
                                <button onClick={() => setModal({ open: true, type: "block", employee: emp })} style={{
                                    background: "none", border: "none", cursor: "pointer", padding: 0,
                                    fontSize: 12, fontWeight: 600, color: T.accent, fontFamily: T.font,
                                }}>{emp.isActive ? "Block" : "Unblock"}</button>
                            </div>
                            {/* Delete */}
                            <div>
                                <button onClick={() => setModal({ open: true, type: "delete", employee: emp })} style={{
                                    background: "none", border: "none", cursor: "pointer", padding: 0,
                                    fontSize: 12, fontWeight: 600, color: T.accent, fontFamily: T.font,
                                }}>Delete</button>
                            </div>
                            {/* View Detail */}
                            <div>
                                <button onClick={() => navigate(`/employees/view/${emp.staffId}`)} style={{
                                    background: "none", border: "none", cursor: "pointer", padding: 0,
                                    fontSize: 12, fontWeight: 600, color: T.accent, fontFamily: T.font,
                                }}>View</button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <ConfirmModal open={modal.open && modal.type === "block"} icon={modal.employee?.isActive ? "block" : "check_circle"}
                iconColor={modal.employee?.isActive ? T.orange : T.green}
                title={modal.employee?.isActive ? "Block Employee?" : "Unblock Employee?"}
                message={modal.employee?.isActive ? "This will restrict their access until unblocked." : "This will restore their access."}
                confirmLabel={modal.employee?.isActive ? "Block" : "Unblock"} confirmColor={modal.employee?.isActive ? T.orange : T.green}
                onConfirm={handleBlockUnblock} onCancel={closeModal} loading={actionLoading} />
            <ConfirmModal open={modal.open && modal.type === "delete"} icon="delete" iconColor={T.red}
                title="Delete Employee?" message="This action is irreversible. All data will be permanently removed."
                confirmLabel="Delete" confirmColor={T.red} onConfirm={handleDelete} onCancel={closeModal} loading={actionLoading} />
        </div>
    );
}

/* ═══ Route-based Page Components ═══ */

/* Add Employee Page — rendered at /employees/add */
function EmployeeAddPage() {
    const ctx = useEmployeeCtx();
    const navigate = useNavigate();
    if (!ctx) return null;
    const { token, locationId, merchantId, businessName, branches, currentBranchName, fetchEmployees, mobile, loggedInStaffId } = ctx;
    return (
        <AddEmployeePanel
            open={true}
            onClose={() => navigate("/employees")}
            token={token} locationId={locationId} merchantId={merchantId} businessName={businessName}
            branches={branches} currentBranchName={currentBranchName} mobile={mobile}
            actionPerformedByStaffId={loggedInStaffId}
        />
    );
}

/* Edit Employee Page — rendered at /employees/:id/edit */
function EmployeeEditPage() {
    const ctx = useEmployeeCtx();
    const navigate = useNavigate();
    const { id } = useParams();

    if (!ctx) return null;
    const { token, locationId, merchantId, businessName, branches, currentBranchName, fetchEmployees, mobile, employees, loggedInStaffId } = ctx;

    // Pass cached employee if available (for name display in breadcrumb), otherwise minimal object with staffId.
    // EditEmployeePanel will always fetch full details from API internally.
    const cachedEmployee = employees.find(e => e.staffId === id) || { staffId: id };

    return (
        <EditEmployeePanel
            open={true}
            employee={cachedEmployee}
            onClose={() => navigate("/employees")}
            token={token} locationId={locationId} merchantId={merchantId} businessName={businessName}
            branches={branches} currentBranchName={currentBranchName} mobile={mobile}
            actionPerformedByStaffId={loggedInStaffId}
        />
    );
}

/* View Employee Page — rendered at /employees/:id/view */
function EmployeeViewPage() {
    const ctx = useEmployeeCtx();
    const navigate = useNavigate();
    const { id } = useParams();
    const [employee, setEmployee] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [permissions, setPermissions] = useState([]);
    const [permissionsLoading, setPermissionsLoading] = useState(true);
    const [modal, setModal] = useState({ open: false, type: "", employee: null });
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        const token = ctx?.token;
        const merchantId = ctx?.merchantId;
        if (!token || !merchantId || !id) {
            setLoading(false);
            setPermissionsLoading(false);
            return;
        }
        let cancelled = false;

        // Show cached employee immediately while fetching
        const cached = (ctx?.employees || []).find(e => e.staffId === id);
        if (cached) setEmployee(cached);

        // Fetch full details — deduplicated across StrictMode double-invoke
        if (!employeeDetailInFlight.has(id)) {
            employeeDetailInFlight.set(id, getEmployeeById({ staffId: id, merchantId, token }));
        }
        employeeDetailInFlight.get(id)!.then(res => {
            employeeDetailInFlight.delete(id);
            if (cancelled) return;
            let emp = null;
            if (res && typeof res === "object") {
                if (res.staffId) emp = res;
                else if (res.metaDataInfo?.data?.staffId) emp = res.metaDataInfo.data;
                else if (res.data?.staffId) emp = res.data;
                else if (Array.isArray(res.content) && res.content[0]?.staffId) emp = res.content[0];
                else emp = res.metaDataInfo?.data ?? res.data ?? res;
            }
            if (emp && typeof emp === "object") {
                setEmployee(prev => ({ ...(prev || {}), ...emp }));
            }
        }).catch(() => {
            employeeDetailInFlight.delete(id);
            if (cancelled) return;
            if (!cached) setEmployee(null);
        }).finally(() => { if (!cancelled) setLoading(false); });

        // Fetch permissions — deduplicated
        if (!staffPermissionsInFlight.has(id)) {
            staffPermissionsInFlight.set(id, getStaffPermissions({ staffId: id, merchantId, token }));
        }
        staffPermissionsInFlight.get(id)!.then(res => {
            staffPermissionsInFlight.delete(id);
            if (cancelled) return;
            let perms = null;
            if (res && typeof res === "object") {
                if (Array.isArray(res)) perms = res;
                else if (Array.isArray(res.metaDataInfo?.data)) perms = res.metaDataInfo.data;
                else if (Array.isArray(res.data)) perms = res.data;
                else if (Array.isArray(res.content)) perms = res.content;
                else if (res.metaDataInfo?.data) perms = res.metaDataInfo.data;
                else if (res.data) perms = res.data;
                else if (Array.isArray(res.modules)) perms = res.modules;
                else if (Array.isArray(res.roles)) perms = res.roles;
                else if (Array.isArray(res.permissions)) perms = res.permissions;
                else perms = res;
            }
            if (perms && typeof perms === "object" && !Array.isArray(perms)) {
                const keys = Object.keys(perms);
                if (keys.length > 0 && keys.every(k => typeof perms[k] === "object")) {
                    perms = keys.map(k => {
                        const val = perms[k];
                        if (Array.isArray(val)) return { module: k, functionality: val };
                        if (val.functionality || val.functions) return { module: k, ...val };
                        if (typeof val === "object" && !Array.isArray(val)) {
                            const funcs = Object.keys(val).map(fk => ({
                                name: fk, displayName: fk.replace(/([A-Z])/g, " $1").trim(),
                                isEnabled: val[fk] === true || val[fk]?.isEnabled || val[fk]?.enabled || false,
                            }));
                            return { module: k, functionality: funcs };
                        }
                        return { module: k, functionality: [] };
                    });
                }
            }
            if (Array.isArray(perms) && perms.length > 0) {
                setPermissions(perms);
            } else {
                const empData = cached;
                const empRole = empData?.assignedRole || empData?.role;
                if (empRole) {
                    getRolesAndFunctions({ token }).then(rolesRes => {
                        if (cancelled) return;
                        let rolesData = rolesRes;
                        if (rolesRes?.metaDataInfo?.data) rolesData = rolesRes.metaDataInfo.data;
                        else if (rolesRes?.data) rolesData = rolesRes.data;
                        let rolePerms: any[] = [];
                        if (Array.isArray(rolesData)) {
                            const match = rolesData.find((r: any) => r.role === empRole || r.roleName === empRole || r.name === empRole);
                            if (match) rolePerms = match.modules || match.permissions || match.functionality || [];
                        } else if (rolesData && typeof rolesData === "object") {
                            if (rolesData[empRole]) {
                                const rd = rolesData[empRole];
                                rolePerms = Array.isArray(rd) ? rd : (rd.modules || rd.permissions || rd.functionality || []);
                            }
                            if (rolePerms.length === 0 && rolesData.roles) {
                                const match = (Array.isArray(rolesData.roles) ? rolesData.roles : []).find((r: any) => r.role === empRole || r.roleName === empRole);
                                if (match) rolePerms = match.modules || match.permissions || match.functionality || [];
                            }
                        }
                        if (rolePerms.length > 0 && !cancelled) setPermissions(rolePerms);
                    }).catch(() => {});
                }
            }
        }).catch(() => { staffPermissionsInFlight.delete(id); })
          .finally(() => { if (!cancelled) setPermissionsLoading(false); });

        return () => { cancelled = true; };
    }, [ctx?.token, ctx?.merchantId, id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!ctx) return null;
    const { token, fetchEmployees, mobile, loggedInStaffId } = ctx;
    const closeModal = () => setModal({ open: false, type: "", employee: null });

    const handleBlockUnblock = async () => {
        const emp = modal.employee; if (!emp) return;
        setActionLoading(true);
        try { await updateEmployeeStatus({ staffId: emp.staffId, isToBlock: emp.isActive, token, actionPerformedByStaffId: loggedInStaffId }); await fetchEmployees(); navigate("/employees"); } catch { /* */ }
        setActionLoading(false); closeModal();
    };
    const handleDelete = async () => {
        const emp = modal.employee; if (!emp) return;
        setActionLoading(true);
        try { await deleteEmployeeApi({ staffId: emp.staffId, token, actionPerformedByStaffId: loggedInStaffId }); await fetchEmployees(); navigate("/employees"); } catch { /* */ }
        setActionLoading(false); closeModal();
    };

    if (loading && !employee) return (
        /* View page shimmer — labels visible, only values shimmer */
        <div style={{ padding: mobile ? "16px" : "24px 32px", fontFamily: T.font }}>
            {/* Breadcrumb */}
            <Breadcrumb items={[{ label: "Employees" }, { label: "Employee Details" }]} />

            {/* Hero banner shimmer */}
            <div style={{
                background: "linear-gradient(135deg, rgba(229,35,51,0.06) 0%, rgba(229,35,51,0.02) 100%)",
                border: `1px solid ${T.cardBorder}`, borderRadius: 14,
                padding: mobile ? 16 : "24px 28px", display: "flex", alignItems: "center", gap: 16, marginBottom: 24,
            }}>
                <Bone w={56} h={56} r={14} />
                <div style={{ flex: 1 }}><Bone w={180} h={20} r={4} mb={6} /><Bone w={120} h={13} r={4} /></div>
                <div style={{ display: "flex", gap: 8 }}>
                    <Bone w={70} h={34} r={10} /><Bone w={70} h={34} r={10} /><Bone w={70} h={34} r={10} />
                </div>
            </div>

            {/* 2-col detail cards — real labels, shimmer values */}
            <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 20, marginBottom: 24 }}>
                {/* Personal Info */}
                <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: mobile ? 16 : "20px 24px", boxShadow: T.cardShadow }}>
                    <SectionHeader icon="person" title="Personal Info" subtitle="Employee personal details" />
                    {["Full Name", "Nick Name", "Email", "Phone", "Address", "Education", "Date of Birth"].map((label, i) => (
                        <div key={i} style={{ display: "flex", padding: "10px 0", borderBottom: `1px solid ${T.divider}` }}>
                            <div style={{ width: mobile ? 110 : 160, flexShrink: 0, fontSize: 12, fontWeight: 500, color: T.t3 }}>{label}</div>
                            <Bone w="55%" h={13} r={3} />
                        </div>
                    ))}
                </div>
                {/* Formal Setup */}
                <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: mobile ? 16 : "20px 24px", boxShadow: T.cardShadow }}>
                    <SectionHeader icon="settings" title="Formal Setup" subtitle="Role and access information" />
                    {["Staff ID", "User ID", "Assigned Role", "Assigned Outlet", "Device Pin", "Status"].map((label, i) => (
                        <div key={i} style={{ display: "flex", padding: "10px 0", borderBottom: `1px solid ${T.divider}` }}>
                            <div style={{ width: mobile ? 110 : 160, flexShrink: 0, fontSize: 12, fontWeight: 500, color: T.t3 }}>{label}</div>
                            <Bone w="50%" h={13} r={3} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
    if (!employee) return (
        <div style={{ padding: 56, textAlign: "center", color: T.t3 }}>
            <Icon name="error_outline" size={48} color={T.t3} style={{ display: "block", margin: "0 auto 12px" }} />
            <div style={{ fontSize: 16, fontWeight: 700, color: T.t1, marginBottom: 8 }}>Employee Not Found</div>
            <button onClick={() => navigate("/employees")} style={{
                padding: "10px 20px", borderRadius: 10, border: "none", background: T.accent, color: "#fff",
                fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.font,
            }}>Back to Employees</button>
        </div>
    );

    return (
        <>
            <ViewEmployeePanel
                open={true}
                employee={employee}
                onClose={() => navigate("/employees")}
                onEdit={(emp) => navigate(`/employees/edit/${emp.staffId}`)}
                onBlock={(emp) => setModal({ open: true, type: "block", employee: emp })}
                onDelete={(emp) => setModal({ open: true, type: "delete", employee: emp })}
                mobile={mobile}
                permissions={permissions}
                permissionsLoading={permissionsLoading}
            />
            <ConfirmModal open={modal.open && modal.type === "block"} icon={modal.employee?.isActive ? "block" : "check_circle"}
                iconColor={modal.employee?.isActive ? T.orange : T.green}
                title={modal.employee?.isActive ? "Block Employee?" : "Unblock Employee?"}
                message={modal.employee?.isActive ? "This will restrict their access until unblocked." : "This will restore their access."}
                confirmLabel={modal.employee?.isActive ? "Block" : "Unblock"} confirmColor={modal.employee?.isActive ? T.orange : T.green}
                onConfirm={handleBlockUnblock} onCancel={closeModal} loading={actionLoading} />
            <ConfirmModal open={modal.open && modal.type === "delete"} icon="delete" iconColor={T.red}
                title="Delete Employee?" message="This action is irreversible. All data will be permanently removed."
                confirmLabel="Delete" confirmColor={T.red} onConfirm={handleDelete} onCancel={closeModal} loading={actionLoading} />
        </>
    );
}

/* ═══ Employee Layout — wraps all /employees/* routes with sidebar + shared context ═══ */
export default function EmployeeLayout() {
    const isDesktop = useIsDesktop();
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    const setTopBarProps = useDesktopTopBar();
    const location = useLocation();

    const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? findLocationId(auth) ?? "", [auth, branch]);
    const token = useMemo(() => (auth ? findToken(auth) : null), [auth]);
    const loggedInStaffId = useMemo(() => auth?.staff_id || auth?.staffId || auth?.id || "", [auth]);
    const merchantId = useMemo(() => {
        if (!auth) return "";
        return auth.merchantId || auth.credentials?.merchantId || "";
    }, [auth]);
    const businessName = useMemo(() => {
        if (!auth) return "";
        return auth.businessName || auth.credentials?.businessName || "";
    }, [auth]);
    const currentBranchName = branch?.locationName ?? branch?.name ?? branch?.branchName ?? "";
    const [employees, setEmployees] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [noAccess, setNoAccess] = useState(false);
    // Shared fetch error consumed by EmployeeEmptyState for retry/internet messaging.
    const [fetchError, setFetchError] = useState("");

    const fetchEmployees = useCallback(async () => {
        if (!locationId || !token) { setLoading(false); setFetchError(""); return; }
        if (employeeListInFlight.has(locationId)) {
            setLoading(true); setNoAccess(false); setFetchError("");
            try {
                const data = await employeeListInFlight.get(locationId);
                setEmployees(Array.isArray(data) ? data : []);
                setFetchError("");
            } catch (err) {
                if (err?.status === 403) {
                    setNoAccess(true);
                    setFetchError("");
                } else {
                    setFetchError(getEmployeeFetchErrorMessage(err));
                }
                setEmployees([]);
            } finally { setLoading(false); }
            return;
        }
        setLoading(true); setNoAccess(false); setFetchError("");
        const req = (async () => {
            const res = await getEmployeeList({ locationId, token });
            let data = res;
            if (Array.isArray(res)) data = res;
            else if (res?.metaDataInfo?.data && Array.isArray(res.metaDataInfo.data)) data = res.metaDataInfo.data;
            else if (res?.data && Array.isArray(res.data)) data = res.data;
            else if (res?.content && Array.isArray(res.content)) data = res.content;
            else if (res && typeof res === "object" && !Array.isArray(res)) {
                const arrKey = Object.keys(res).find(k => Array.isArray(res[k]));
                data = arrKey ? res[arrKey] : [];
            }
            return Array.isArray(data) ? data : [];
        })();
        employeeListInFlight.set(locationId, req);
        try {
            const data = await req;
            setEmployees(data);
            setFetchError("");
        } catch (err) {
            if (err?.status === 403) {
                setNoAccess(true);
                setFetchError("");
            } else {
                setFetchError(getEmployeeFetchErrorMessage(err));
            }
            setEmployees([]);
        } finally {
            setLoading(false);
            employeeListInFlight.delete(locationId);
        }
    }, [locationId, token]);

    // Fetch branches — reuse DesktopLayout's shared cache to avoid duplicate network requests
    useEffect(() => {
        if (!locationId || !token) return;
        let cancelled = false;
        const cacheKey = String(locationId);
        const applyBranches = (payload: any) => {
            if (cancelled) return;
            const list = payload?.branch ?? payload?.branches ?? [];
            if (Array.isArray(list) && list.length > 0) setBranches(list);
            else if (currentBranchName) setBranches([{ locationName: currentBranchName, id: locationId }]);
        };
        const cached = locationDetailsCache.get(cacheKey);
        if (cached) { applyBranches(cached); return () => { cancelled = true; }; }
        const inFlight = locationDetailsInFlight.get(cacheKey);
        if (inFlight) {
            inFlight.then(applyBranches).catch(() => { if (!cancelled && currentBranchName) setBranches([{ locationName: currentBranchName, id: locationId }]); });
            return () => { cancelled = true; };
        }
        const req = getLocationDetails(locationId, token).then(res => {
            const payload = res?.metaDataInfo?.data ?? res?.data ?? res;
            if (payload) locationDetailsCache.set(cacheKey, payload);
            return payload;
        }).finally(() => locationDetailsInFlight.delete(cacheKey));
        locationDetailsInFlight.set(cacheKey, req);
        req.then(applyBranches).catch(() => { if (!cancelled && currentBranchName) setBranches([{ locationName: currentBranchName, id: locationId }]); });
        return () => { cancelled = true; };
    }, [locationId, token, currentBranchName]);

    // Only fetch employee list on the list page (index route), not on add/edit/view
    const subPath = location.pathname.replace(/^\/employees\/?/, "").replace(/\/$/, "");
    const isListPage = !subPath || subPath === "";
    useEffect(() => { if (isListPage) fetchEmployees(); }, [fetchEmployees, isListPage]);

    // TopBar title based on route
    const pageTitle = subPath.startsWith("edit") ? "Edit Employee"
        : subPath.startsWith("view") ? "Employee Details"
        : subPath === "add" ? "Add Employee"
        : "Employee Management";

    useEffect(() => {
        setTopBarProps({
            title: pageTitle,
            breadcrumbs: null,
            dateFilters: null,
            subtitle: null,
            showBack: false,
        });
    }, [setTopBarProps, pageTitle]);

    const mobile = !isDesktop;
    const ctxValue = useMemo(() => ({
        token, locationId, merchantId, businessName, currentBranchName,
        employees, branches, fetchEmployees, mobile, loading, noAccess, fetchError, loggedInStaffId,
    }), [token, locationId, merchantId, businessName, currentBranchName, employees, branches, fetchEmployees, mobile, loading, noAccess, fetchError, loggedInStaffId]);

    if (isDesktop) {
        return (
            <DesktopLayout>
                <EmployeeCtx.Provider value={ctxValue}>
                    <Outlet />
                </EmployeeCtx.Provider>
            </DesktopLayout>
        );
    }

    return (
        <Phone>
            <TopBar variant="mobile" />
            <div style={{ flex: 1, overflowY: "auto" }}>
                <EmployeeCtx.Provider value={ctxValue}>
                    <Outlet />
                </EmployeeCtx.Provider>
            </div>
            <BottomNav />
        </Phone>
    );
}

/* ═══ Employee List Page — rendered at /employees (index route) ═══ */
export function EmployeeListPage() {
    const isDesktop = useIsDesktop();
    return <EmployeeContent mobile={!isDesktop} />;
}

/* ═══ Named exports for route components ═══ */
export { EmployeeAddPage, EmployeeEditPage, EmployeeViewPage };
